/**
 * API-Client — `callBackend(endpoint, payload)` analog `App_Modul_15_BackendApi.js`.
 *
 * Pattern:
 *   - Generic-typed: callBackend<T>(...) → T mit Aufrufer-Type-Safety
 *   - Bearer-Token aus localStorage via auth/token.ts
 *   - Auto-Logout bei 401 (Server-Side-Revoke / Token-Expiry)
 *   - ApiError mit code + message + statusCode + auditId + field + currentState
 *     für detailliertes UI-Error-Handling
 *
 * Drift zu Apps-Script BackendApi.js:
 *   - Apps-Script nutzt `google.script.run.withSuccessHandler(...).backendCall(...)`
 *     — hier durch native fetch ersetzt
 *   - Apps-Script kein Bearer (Session via Apps-Script-Engine) — hier Bearer in
 *     Authorization-Header
 *   - Apps-Script kein context.source per Default — hier `"app_normal"` als Default
 *
 * Phase-3.5-Note: AbortController NICHT implementiert (Justin-Default — YAGNI
 * bis Race-Conditions auftauchen).
 */

import type {
  ApiResponseOk,
  ApiResponseError,
  AppBootstrapResult,
  CallerBilanzResult,
  CallerOverviewResult,
  ClosingsListeResult,
  ClosingsOverviewResult,
  DqOverviewResult,
  FunnelPerformanceResult,
  FunnelStatusResult,
  SlotPerformanceResult,
  VertriebUebersichtResult,
  ZieleResult,
  FuKalenderResult,
  FuOverviewResult,
  MeetingsOverviewResult,
  MeetingsKalenderResult,
  CallerPflegeResult,
  PflegluekenAggregatedResult,
  UserContext,
  LogoutResult,
  MaListResult
} from "./types";
import { clearToken, getToken } from "../auth/token";
import { redirectToLogin } from "../auth/login";

// -----------------------------------------------------------------------------
// Worker-URL — Production-Default, lokal überschreibbar via VITE_WORKER_URL
// -----------------------------------------------------------------------------

// E11b (Weg c): leerer Default = RELATIVE Pfade. Die API läuft über den
// Pages-Function-Proxy (`frontend/functions/api/[[path]].ts`) auf derselben
// Origin wie das Frontend — dadurch ist das Session-Cookie First-Party.
// Vorher stand hier die Worker-URL fest verdrahtet, was jeden API-Call
// cross-site machte und die Cookie-Migration blockierte.
//
// `VITE_WORKER_URL` überschreibt weiterhin (lokale Entwicklung gegen den
// Prod-Worker ohne lokalen Proxy).
const PROXY_SAME_ORIGIN = "";

export const WORKER_URL: string =
  (import.meta.env.VITE_WORKER_URL as string | undefined) ?? PROXY_SAME_ORIGIN;

// -----------------------------------------------------------------------------
// Test-Telemetrie (Frontend-Funktionstest) — protokolliert jeden API-Call.
// Nur aktiv wenn localStorage `ct_test_log === "1"` → null Einfluss auf Normalbetrieb.
// `ctTestReport()` in der Browser-Console dumpt + kopiert das Log (an Claude geben).
// -----------------------------------------------------------------------------
type TestLogEntry = { t: string; view: string; endpoint: string; status: number; ok: boolean; error: string | null; ms: number | null };
if (typeof window !== "undefined") {
  const w = window as unknown as { __ctTestLog?: TestLogEntry[]; ctTestReport?: () => TestLogEntry[] };
  w.__ctTestLog = w.__ctTestLog ?? [];
  w.ctTestReport = () => {
    const log = w.__ctTestLog ?? [];
    try { void navigator.clipboard?.writeText(JSON.stringify(log)); } catch { /* clipboard evtl. blockiert */ }
    const errs = log.filter((e) => !e.ok);
    console.log(`[ctTestReport] ${log.length} Calls (${errs.length} Fehler) — JSON in Zwischenablage. An Claude geben.`);
    if (errs.length) console.table(errs);
    return log;
  };
}
function recordTest(endpoint: string, status: number, ok: boolean, error?: string | null, ms?: number): void {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem("ct_test_log") !== "1") return;
    const w = window as unknown as { __ctTestLog: TestLogEntry[] };
    w.__ctTestLog.push({ t: new Date().toISOString(), view: location.hash || location.pathname, endpoint, status, ok, error: error ?? null, ms: ms ?? null });
  } catch { /* ignore */ }
}

// -----------------------------------------------------------------------------
// ApiError — wird bei jedem nicht-2xx-Response geworfen (außer 401 → Auto-Logout)
// -----------------------------------------------------------------------------

export class ApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly auditId?: string;
  public readonly field?: string;
  public readonly issues?: Array<{ field: string; message: string }>;
  public readonly currentState?: Record<string, unknown>;

  constructor(opts: {
    code: string;
    message: string;
    statusCode: number;
    auditId?: string;
    field?: string;
    issues?: Array<{ field: string; message: string }>;
    currentState?: Record<string, unknown>;
  }) {
    super(opts.message);
    this.name = "ApiError";
    this.code = opts.code;
    this.statusCode = opts.statusCode;
    if (opts.auditId !== undefined) this.auditId = opts.auditId;
    if (opts.field !== undefined) this.field = opts.field;
    if (opts.issues !== undefined) this.issues = opts.issues;
    if (opts.currentState !== undefined) this.currentState = opts.currentState;
  }
}

// -----------------------------------------------------------------------------
// callBackend — generic-typed Wrapper um fetch
// -----------------------------------------------------------------------------

export type CallOptions = {
  /** Override für context.source (Default: "app_normal"). */
  contextSource?: import("./types").ContextSource;
  /** Falls true → kein Auto-Logout bei 401 (z.B. für /me-Probe). */
  skipAutoLogout?: boolean;
  /** Falls true → kein Token mitschicken (für public endpoints). */
  noAuth?: boolean;
};

export async function callBackend<T = unknown>(
  endpoint: string,
  payload: Record<string, unknown> = {},
  options: CallOptions = {}
): Promise<T> {
  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
  try {
    const result = await _callBackendImpl<T>(endpoint, payload, options);
    recordTest(endpoint, 200, true, null, Math.round(now() - t0));
    return result;
  } catch (err) {
    const sc = err instanceof ApiError ? err.statusCode : 0;
    const code = err instanceof ApiError ? err.code : "EXCEPTION";
    recordTest(endpoint, sc, false, code, Math.round(now() - t0));
    throw err;
  }
}

async function _callBackendImpl<T = unknown>(
  endpoint: string,
  payload: Record<string, unknown> = {},
  options: CallOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json"
  };

  if (!options.noAuth) {
    const token = getToken();
    if (!token) {
      // Kein Token vorhanden — sofort zum Login
      redirectToLogin();
      throw new ApiError({
        code: "NO_TOKEN",
        message: "Nicht eingeloggt — Redirect zum Login.",
        statusCode: 401
      });
    }
    headers["Authorization"] = `Bearer ${token}`;
  }

  const body = JSON.stringify({
    payload,
    context: { source: options.contextSource ?? "app_normal" }
  });

  const url = `${WORKER_URL}${endpoint}`;

  let res: Response;
  try {
    res = await fetch(url, { method: "POST", headers, body });
  } catch (err) {
    // Netzwerk-Fehler (CORS, offline, DNS)
    throw new ApiError({
      code: "NETWORK_ERROR",
      message: `Backend nicht erreichbar: ${(err as Error).message}`,
      statusCode: 0
    });
  }

  // Spezialfall 401: Token revoked oder abgelaufen — Auto-Logout
  if (res.status === 401 && !options.skipAutoLogout) {
    clearToken();
    redirectToLogin();
    throw new ApiError({
      code: "AUTH_EXPIRED",
      message: "Session abgelaufen — Redirect zum Login.",
      statusCode: 401
    });
  }

  // Versuche JSON zu parsen — auch bei Error-Responses, weil Worker
  // standardisierten Envelope liefert
  let data: ApiResponseOk<T> | ApiResponseError;
  try {
    data = (await res.json()) as ApiResponseOk<T> | ApiResponseError;
  } catch {
    throw new ApiError({
      code: "INVALID_RESPONSE",
      message: `Server lieferte kein JSON (HTTP ${res.status})`,
      statusCode: res.status
    });
  }

  if (data.status === "error") {
    throw new ApiError({
      code: data.error.code,
      message: data.error.message ?? `Backend-Fehler ${data.error.code}`,
      statusCode: res.status,
      ...(data.error.audit_id !== undefined && { auditId: data.error.audit_id }),
      ...(data.error.field !== undefined && { field: data.error.field }),
      ...(data.error.issues !== undefined && { issues: data.error.issues }),
      ...(data.error.current_state !== undefined && { currentState: data.error.current_state })
    });
  }

  // R1 (E2): warning-Feld des Envelopes durchreichen — z. B. PARTIAL_DATA, wenn
  // eine Neben-Quelle der View degradiert ist. Das Banner-Modul (lib/data-banner)
  // hört auf dieses Event und zeigt einen persistenten Hinweis; vorher wurde die
  // Warnung hier verworfen und kam nie beim UI an.
  if (data.warning) {
    try {
      window.dispatchEvent(new CustomEvent("api:warning", { detail: { endpoint, warning: data.warning } }));
    } catch { /* Banner ist best-effort */ }
  }

  return data.result;
}

// -----------------------------------------------------------------------------
// Convenience-Wrapper für die Auth-Endpoints
// -----------------------------------------------------------------------------

/**
 * GET /api/v1/auth/me — Worker erwartet GET, aber callBackend macht POST.
 * Workaround: direkter fetch mit GET. Returnt UserContext oder wirft 401.
 */
export async function getUserContext(): Promise<UserContext> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({
      code: "NO_TOKEN",
      message: "Nicht eingeloggt",
      statusCode: 401
    });
  }

  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/auth/me`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    throw new ApiError({
      code: "NETWORK_ERROR",
      message: `Backend nicht erreichbar: ${(err as Error).message}`,
      statusCode: 0
    });
  }

  if (res.status === 401) {
    clearToken();
    redirectToLogin();
    throw new ApiError({ code: "AUTH_EXPIRED", message: "Session abgelaufen", statusCode: 401 });
  }

  const data = (await res.json()) as ApiResponseOk<UserContext> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({
      code: data.error.code,
      message: data.error.message ?? "Auth-Fehler",
      statusCode: res.status
    });
  }
  return data.result;
}

// -----------------------------------------------------------------------------
// Profil-Selbstzuordnung (Phase 7.7) — Workspace-SSO Claim-Flow
// -----------------------------------------------------------------------------

export type ClaimProfile = { person_id: string; vollname: string; requires_approval: boolean };
export type ClaimProfilesResult = { already_linked: boolean; email: string; profiles: ClaimProfile[] };
export type ClaimSubmitResult = {
  status: "linked" | "pending" | "already_linked";
  person_id?: string;
  vollname?: string;
  reason?: string;
  claim_id?: string;
};
export type ProfileClaim = {
  id: string;
  email: string;
  person_id: string;
  vollname: string;
  status: string;
  reason: string;
  requested_at: number;
  alt_email?: string;
};

/**
 * GET /api/v1/auth/claim/profiles — wählbare Profile (Login-Flow ohne MA-Eintrag).
 * Raw-Fetch (Endpoint liest kein callBackend-Envelope). Wirft ApiError; 403 =
 * keine Workspace-Mail / Claim deaktiviert (Caller → normaler Login).
 */
export async function claimProfiles(): Promise<ClaimProfilesResult> {
  const token = getToken();
  if (!token) throw new ApiError({ code: "NO_TOKEN", message: "Kein Token", statusCode: 401 });
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/auth/claim/profiles`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: (err as Error).message, statusCode: 0 });
  }
  const data = (await res.json().catch(() => ({ status: "error", error: { code: "INVALID_RESPONSE", message: "kein JSON" } }))) as
    | ApiResponseOk<ClaimProfilesResult>
    | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Claim-Fehler", statusCode: res.status });
  }
  return data.result;
}

/**
 * POST /api/v1/auth/claim/submit — verknüpft die Login-Mail mit dem Profil bzw.
 * erzeugt eine Admin-Freigabe-Anfrage. Raw-Body { person_id } (kein Envelope).
 */
export async function claimSubmit(personId: string): Promise<ClaimSubmitResult> {
  const token = getToken();
  if (!token) throw new ApiError({ code: "NO_TOKEN", message: "Kein Token", statusCode: 401 });
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/auth/claim/submit`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ person_id: personId })
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: (err as Error).message, statusCode: 0 });
  }
  const data = (await res.json().catch(() => ({ status: "error", error: { code: "INVALID_RESPONSE", message: "kein JSON" } }))) as
    | ApiResponseOk<ClaimSubmitResult>
    | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Claim-Fehler", statusCode: res.status });
  }
  return data.result;
}

/** POST /api/v1/admin/claim/pending — offene Profil-Zuordnungs-Anfragen (Admin). */
export async function adminClaimPending(): Promise<{ claims: ProfileClaim[] }> {
  return await callBackend<{ claims: ProfileClaim[] }>("/api/v1/admin/claim/pending", {});
}

/** POST /api/v1/admin/claim/decide — Freigabe (approve=true) oder Ablehnung. */
export async function adminClaimDecide(
  id: string,
  approve: boolean
): Promise<{ id: string; status: "approved" | "rejected"; person_id?: string; vollname?: string }> {
  return await callBackend("/api/v1/admin/claim/decide", { id, approve });
}

/**
 * POST /api/v1/app/bootstrap — Hub-Bootstrap (Phase 3.5 P0-8).
 * Returnt user_context + mitarbeiter_map + hub_context + Konstanten in 1 Call.
 */
export async function getAppBootstrap(): Promise<AppBootstrapResult> {
  return await callBackend<AppBootstrapResult>("/api/v1/app/bootstrap", {});
}

/** Anzeige-Datumsformat (System-Setting DATE_FORMAT) — einmal beim App-Load. */
export async function appDateFormat(): Promise<{
  date_format: "de" | "iso";
  liga_live?: boolean;
  /** Ausbaustufe 4: eigener Schalter, unabhängig von `liga_live`. */
  arena_live?: boolean;
  buchung_sichtbar?: boolean;
}> {
  return await callBackend<{
    date_format: "de" | "iso";
    liga_live?: boolean;
    arena_live?: boolean;
    buchung_sichtbar?: boolean;
  }>("/api/v1/app/date-format", {});
}

/** Konfigurierbare Outcome-Anzeigenamen (M1–M8 + F1–F9) — einmal beim App-Load. */
export async function appOutcomeLabels(): Promise<{ outcome_labels: Record<string, string> }> {
  return await callBackend<{ outcome_labels: Record<string, string> }>("/api/v1/app/outcome-labels", {});
}

/**
 * POST /api/v1/b4/readPflegluekenAggregated — Pflegelücken-View (Phase 3.5 §5).
 */
export async function readPflegluekenAggregated(
  params: { for_person?: string } = {}
): Promise<PflegluekenAggregatedResult> {
  return await callBackend<PflegluekenAggregatedResult>(
    "/api/v1/b4/readPflegluekenAggregated",
    params
  );
}

/**
 * POST /api/v1/b3/readFuOverviewAggregated — FU-Overview (Phase 3.5 §5 Sprint 2).
 */
export async function readFuOverviewAggregated(
  params: { range?: string; start_date?: string | null; end_date?: string | null; for_person?: string } = {}
): Promise<FuOverviewResult> {
  return await callBackend<FuOverviewResult>(
    "/api/v1/b3/readFuOverviewAggregated",
    params
  );
}

/**
 * POST /api/v1/b2/readMeetingsOverviewAggregated — Meetings-Overview (Sprint 8a).
 */
export async function readMeetingsOverviewAggregated(
  params: { range?: string; start_date?: string | null; end_date?: string | null; for_person?: string } = {}
): Promise<MeetingsOverviewResult> {
  return await callBackend<MeetingsOverviewResult>(
    "/api/v1/b2/readMeetingsOverviewAggregated",
    params
  );
}

/**
 * POST /api/v1/b1/readCallerPflegeAggregated — Caller-Pflege (Sprint 9).
 */
export async function readCallerPflegeAggregated(
  params: { person_id?: string; iso_jahr?: number; iso_woche?: number; history_weeks?: number; trend_weeks?: number } = {}
): Promise<CallerPflegeResult> {
  return await callBackend<CallerPflegeResult>(
    "/api/v1/b1/readCallerPflegeAggregated",
    params
  );
}

/**
 * POST /api/v1/b1/writeCallerEntry — Tages-Eintrag speichern.
 */
export async function writeCallerEntry(payload: {
  person_id: string;
  datum: string;
  pa?: number;
  pg?: number;
  mt?: number;
  nt?: number;
  notiz?: string;
  clear?: boolean; // Tag leeren (Zeile löschen) — erlaubt Summe 0
}): Promise<{ status: string }> {
  return await callBackend("/api/v1/b1/writeCallerEntry", payload);
}

/**
 * POST /api/v1/b1/writeAbwesenheit — Abwesenheits-Typ pro Datum.
 */
export async function writeAbwesenheit(payload: {
  person_id: string;
  datum: string;
  typ: string | null;
}): Promise<{ status: string }> {
  return await callBackend("/api/v1/b1/writeAbwesenheit", payload);
}

/**
 * POST /api/v1/b2/writeMeetingEntry — Neues Meeting anlegen (Sprint 11).
 */
export async function writeMeetingEntry(payload: {
  company_id: string;
  datum: string;
  uhrzeit: string;
  host_person_id: string;
  caller_person_id: string;
  termin_typ?: string;
  anzahl_teams_range?: string;
  verein_anzeigename?: string;
  notiz?: string;
  reschedule_vorgaenger_id?: string; // M6: Link aufs alte Meeting beim Reschedule
}): Promise<{ action: string; meeting_id: string; audit_id: string }> {
  return await callBackend("/api/v1/b2/writeMeetingEntry", payload);
}

/**
 * POST /api/v1/b2/rescheduleMeeting — altes Meeting auf M6 + neues Meeting mit
 * `Reschedule_Vorgaenger_ID`, **ein Vorgang**.
 *
 * 🔑 Ersetzt den Zwei-Schritt `updateMeetingOutcome(M6)` + `writeMeetingEntry(…)`:
 * dazwischen konnte ein Meeting „verschoben" heißen, ohne dass es ein Ziel gab.
 * `host_person_id` ist optional — der Regelweg bucht einen TERMIN, keinen HOST;
 * gesetzt trägt es den Host-Wechsel (Einspringen).
 */
export async function rescheduleMeeting(payload: {
  meeting_id: string;
  neues_datum: string;
  neue_uhrzeit: string;
  neuer_host_person_id?: string;
  slot_override?: boolean;
  override_grund?: string;
  notiz?: string;
  meeting_link?: string;
}): Promise<{ action: string; old_meeting_id: string; new_meeting_id: string }> {
  return await callBackend("/api/v1/b2/rescheduleMeeting", payload);
}

/** POST /api/v1/b3/rescheduleFu — alte FU auf F6 + neue F1-FU (frei gebucht, Datum+Uhrzeit). */
export async function rescheduleFu(payload: {
  fu_id: string;
  neues_fu_datum: string;
  neues_fu_uhrzeit?: string;
  neuer_fu_host_person_id: string;
  notiz?: string;
  /** Feature #30: optionaler Meeting-Link (Meet/Zoom) für den neuen FU-Termin. */
  meeting_link?: string;
}): Promise<{ action: string; old_fu_id: string; new_fu_id: string }> {
  return await callBackend("/api/v1/b3/rescheduleFu", payload);
}

/**
 * POST /api/v1/b3/writeFuEntry — Neuen Follow-Up anlegen (Sprint 11).
 */
export async function writeFuEntry(payload: {
  meeting_id: string;
  company_id: string;
  fu_datum: string;
  fu_uhrzeit?: string;
  fu_host_person_id: string;
  caller_person_id?: string;
  verein_anzeigename?: string;
  notiz?: string;
  /** Feature #30: optionaler Meeting-Link (Meet/Zoom). */
  meeting_link?: string;
  /** F2-Folge-FU: Verkettung mit dem auslösenden FU (Spalte V). */
  vorgaenger_fu_id?: string;
}): Promise<{ action: string; fu_id: string; fu_no: number; audit_id: string }> {
  return await callBackend("/api/v1/b3/writeFuEntry", payload);
}

export type HsCompanyMatch = { company_id: string; name: string; city: string; zip: string; state: string };

/** POST /api/v1/lookup/hubspot-companies — Live-Vereins-Suche in HubSpot (Sprint 11). */
export async function searchHubSpotCompanies(query: string): Promise<{ query: string; matches: HsCompanyMatch[]; total_in_hubspot: number; cached: boolean }> {
  return await callBackend("/api/v1/lookup/hubspot-companies", { query });
}

export type ActivePerson = { person_id: string; anzeigename: string; farbe: string };

/** POST /api/v1/lookup/active-persons — aktive MT-Hosts / FU-Hosts / Caller (Sprint 11). */
export async function getActivePersons(): Promise<{ mt_hosts: ActivePerson[]; fu_hosts: ActivePerson[]; callers: ActivePerson[] }> {
  return await callBackend("/api/v1/lookup/active-persons", {});
}

export type MeetingSearchMatch = {
  meeting_id: string;
  company_id: string;
  verein_anzeigename: string;
  datum: string;
  uhrzeit: string;
  host_anzeigename: string;
  host_person_id?: string;
};

/** POST /api/v1/lookup/meeting-search — Meeting-Suche per Vereinsname fürs FU-Form (Sprint 11). */
export async function searchMeetings(query: string): Promise<{ query: string; matches: MeetingSearchMatch[] }> {
  return await callBackend("/api/v1/lookup/meeting-search", { query });
}

export type AvailableSlot = {
  slot_id: string;
  datum: string;
  uhrzeit: string;
  wochentag: string;
  host_person_id: string;
  host_anzeigename: string;
};

/** POST /api/v1/b2/readAvailableSlots — freie, künftige Slots aus 20_SLOTS für den Meeting-Slot-Picker. */
export async function readAvailableSlots(): Promise<{ slots: AvailableSlot[]; count: number }> {
  return await callBackend("/api/v1/b2/readAvailableSlots", {});
}

/**
 * POST /api/v1/b2/readMeetingsKalenderAggregated — Meetings-Kalender (Sprint 8b).
 */
export async function readMeetingsKalenderAggregated(
  params: { range?: string; start_date?: string; end_date?: string; host_person_id?: string } = {}
): Promise<MeetingsKalenderResult> {
  return await callBackend<MeetingsKalenderResult>(
    "/api/v1/b2/readMeetingsKalenderAggregated",
    params
  );
}

/**
 * POST /api/v1/b2/updateMeetingOutcome — Outcome speichern.
 */
export async function updateMeetingOutcome(payload: {
  meeting_id: string;
  outcome: string;
  outcome_datum?: string;
  fu_datum?: string;
  lost_grund_code?: string;
  lost_grund_freitext?: string;
  notiz?: string;
}): Promise<{ status: string; zeeg_hinweis?: string }> {
  return await callBackend("/api/v1/b2/updateMeetingOutcome", payload);
}

/**
 * POST /api/v1/b2/updateMeetingHost — Host wechseln (vor Outcome).
 */
export async function updateMeetingHost(payload: {
  meeting_id: string;
  new_host_person_id: string;
}): Promise<{ status: string; sperrzeit_warnung?: { von: string; bis: string; notiz: string } }> {
  return await callBackend("/api/v1/b2/updateMeetingHost", payload);
}

/** Status eines Hosts zu einer Stunde — `frei` oder mit Grund in `belegt`. */
export type HostStatusAt = {
  datum: string;
  uhrzeit: string;
  feiertag: boolean;
  frei: Array<{ person_id: string; name: string; im_raster: boolean }>;
  belegt: Array<{ person_id: string; name: string; grund: string }>;
};

/**
 * POST /api/v1/b2/readHostStatusAt — wer kann an diesem Tag zu dieser Stunde?
 *
 * 🔑 **Nicht** `/api/v1/b2/freieHosts`: der liefert dasselbe, steht aber hinter
 * dem Freigabe-Tor der Buchungsstrecke und gäbe jedem Host außer Justin 403.
 */
export async function readHostStatusAt(payload: {
  datum: string;
  uhrzeit: string;
}): Promise<HostStatusAt> {
  return await callBackend("/api/v1/b2/readHostStatusAt", payload);
}

/**
 * POST /api/v1/b2/linkMeetingReschedule — Pflegelücke MEETING_M8 „verbinden":
 * ein bereits bestehendes Meeting (z.B. aus Zeeg) als Reschedule-Nachfolger an
 * das alte M8/M6-Meeting knüpfen. Räumt den M8-Eintrag beim nächsten Cron ab.
 */
export async function linkMeetingReschedule(payload: {
  vorgaenger_meeting_id: string;
  nachfolger_meeting_id: string;
}): Promise<{ action: string; vorgaenger_meeting_id: string; nachfolger_meeting_id: string; audit_id: string }> {
  return await callBackend("/api/v1/b2/linkMeetingReschedule", payload);
}

/**
 * POST /api/v1/b2/updateMeetingOutcomeFromPflegeluecke — Outcome aus Pflegelücke
 * speichern (Edit-Window-Schutz deaktiviert, mit pflegeluecke_row_id).
 */
export async function updateMeetingOutcomeFromPflegeluecke(payload: {
  meeting_id: string;
  outcome: string;
  pflegeluecke_row_id?: string;
  fu_datum?: string;
  lost_grund_code?: string;
  lost_grund_freitext?: string;
  notiz?: string;
}): Promise<{ status: string; zeeg_hinweis?: string }> {
  return await callBackend("/api/v1/b2/updateMeetingOutcomeFromPflegeluecke", payload);
}

/**
 * POST /api/v1/b3/readFuKalenderAggregated — FU-Kalender (Phase 3.5 §5 Sprint 2c).
 */
export async function readFuKalenderAggregated(
  params: { range?: string; start_date?: string; end_date?: string; host_person_id?: string } = {}
): Promise<FuKalenderResult> {
  return await callBackend<FuKalenderResult>(
    "/api/v1/b3/readFuKalenderAggregated",
    params
  );
}

/** POST /api/v1/b3/updateFuOutcome — Outcome speichern (Phase 3.5 §5 Sprint 2c). */
export async function updateFuOutcome(payload: {
  fu_id: string;
  outcome: string;
  lost_grund_code?: string;
  lost_grund_freitext?: string;
  notiz?: string;
  folge_fu_datum?: string;
  folge_fu_uhrzeit?: string;
}): Promise<unknown> {
  // Backend-Schema erwartet `fu_outcome` (nicht `outcome` wie bei B2) → umbenennen.
  const { outcome, ...rest } = payload;
  return await callBackend("/api/v1/b3/updateFuOutcome", { ...rest, fu_outcome: outcome });
}

/** POST /api/v1/b3/updateFuHost — Host wechseln (Phase 3.5 §5 Sprint 2c). */
export async function updateFuHost(payload: {
  fu_id: string;
  new_host_person_id: string;
}): Promise<unknown> {
  return await callBackend("/api/v1/b3/updateFuHost", payload);
}

/** POST /api/v1/b3/updateFuOutcomeFromPflegeluecke — Outcome aus Pflegelücke (Bypass Edit-Window). */
export async function updateFuOutcomeFromPflegeluecke(payload: {
  fu_id: string;
  outcome: string;
  pflegeluecke_row_id?: string;
  lost_grund_code?: string;
  lost_grund_freitext?: string;
  notiz?: string;
  folge_fu_datum?: string;
}): Promise<unknown> {
  // Backend-Schema erwartet `fu_outcome` (nicht `outcome` wie bei B2) → umbenennen.
  const { outcome, ...rest } = payload;
  return await callBackend("/api/v1/b3/updateFuOutcomeFromPflegeluecke", { ...rest, fu_outcome: outcome });
}

/** POST /api/v1/b1/readCallerOverviewAggregated — Caller-Overview (Sprint 3a). */
export async function readCallerOverviewAggregated(
  params: { range?: string; start_date?: string; end_date?: string; for_person?: string } = {}
): Promise<CallerOverviewResult> {
  return await callBackend<CallerOverviewResult>(
    "/api/v1/b1/readCallerOverviewAggregated",
    params
  );
}

/** POST /api/v1/b1/readCallerBilanzAggregated — Caller-Bilanz (Sprint 3b). */
export async function readCallerBilanzAggregated(
  params: { iso_jahr?: number } = {}
): Promise<CallerBilanzResult> {
  return await callBackend<CallerBilanzResult>(
    "/api/v1/b1/readCallerBilanzAggregated",
    params
  );
}

/** POST /api/v1/b4/readClosingsListeAggregated — Closings-Liste (Sprint 5). */
export async function readClosingsListeAggregated(
  params: { range?: string; start_date?: string; end_date?: string } = {}
): Promise<ClosingsListeResult> {
  return await callBackend<ClosingsListeResult>(
    "/api/v1/b4/readClosingsListeAggregated",
    params
  );
}

/** POST /api/v1/b4/markClosingWiderruf — Closing-Widerruf vor Start eintragen. */
export async function markClosingWiderruf(
  input: { closing_id: string; lost_grund_code: string; lost_grund_freitext?: string }
): Promise<{ closing_id: string; widerruf_am: string; company_id?: string; already?: boolean }> {
  return await callBackend("/api/v1/b4/markClosingWiderruf", input as Record<string, unknown>);
}

/**
 * POST /api/v1/b4/setClosingCloser — Closer eines Closings korrigieren (BL/Admin).
 * Setzt HubSpot-Eigenschaft `vertriebler` (= hs_owner_id des MA) + Sheet-Snapshot/Resolved.
 */
export async function setClosingCloser(
  input: { closing_id: string; person_id: string }
): Promise<{ closing_id: string; person_id: string; closer_name: string; company_id?: string; already?: boolean }> {
  return await callBackend("/api/v1/b4/setClosingCloser", input as Record<string, unknown>);
}

/**
 * POST /api/v1/b4/widerruf — #33 „Widerruf vor Start"-App-Aktion.
 * Setzt HubSpot (Leadstatus→Widerruf, Lifecycle→kein Kunde mehr, gekündigt zum =
 * Vertragsbeginn−1, gekündigt am = Eingangsdatum) + markiert das Closing.
 */
export async function setWiderruf(
  payload: { closing_id: string; gekuendigt_am: string; grund?: string }
): Promise<{ closing_id: string; company_id: string; gekuendigt_zum: string; gekuendigt_am: string }> {
  return await callBackend("/api/v1/b4/widerruf", payload as Record<string, unknown>);
}

/**
 * POST /api/v1/b4/widerrufRueckgaengig — eingetragenen „Widerruf vor Start" zurücknehmen.
 * Setzt HubSpot zurück (Leadstatus→Kunde, Lifecycle→Neukunde/Kunde, gekündigt am/zum
 * geleert, Vertragsende = geplantes Ende aus dem Closing) und entflaggt das Closing
 * (zählt wieder in die KPI). `already: true` = war gar nicht widerrufen.
 */
export async function undoWiderruf(
  payload: { closing_id: string; notiz?: string }
): Promise<{ closing_id: string; company_id?: string; lifecycle?: string; already?: boolean }> {
  // E5/N6: kein `vertragsende`/`warnung` mehr — der Widerruf überschreibt das Feld nicht
  // mehr, also gibt es bei der Rücknahme nichts wiederherzustellen oder zu warnen.
  return await callBackend("/api/v1/b4/widerrufRueckgaengig", payload as Record<string, unknown>);
}

/** POST /api/v1/b4/readClosingsOverviewAggregated — Closings-Overview (Sprint 5b). */
export async function readClosingsOverviewAggregated(
  params: { range?: string; start_date?: string; end_date?: string } = {}
): Promise<ClosingsOverviewResult> {
  return await callBackend<ClosingsOverviewResult>(
    "/api/v1/b4/readClosingsOverviewAggregated",
    params
  );
}

/** POST /api/v1/b4/readDqOverviewAggregated — DQ-View (Sprint 7c). */
export async function readDqOverviewAggregated(): Promise<DqOverviewResult> {
  return await callBackend<DqOverviewResult>("/api/v1/b4/readDqOverviewAggregated", {});
}

/** POST /api/v1/b2/readSlotPerformanceAggregated — Meetings-Slots (Sprint 4a). */
export async function readSlotPerformanceAggregated(
  params: { zeitraum_key?: string } = {}
): Promise<SlotPerformanceResult> {
  return await callBackend<SlotPerformanceResult>(
    "/api/v1/b2/readSlotPerformanceAggregated",
    params
  );
}

/** POST /api/v1/b4/readFunnelStatusAggregated — Funnel-Status (Sprint 6a). */
export async function readFunnelStatusAggregated(
  params: { range_days?: number } = {}
): Promise<FunnelStatusResult> {
  return await callBackend<FunnelStatusResult>(
    "/api/v1/b4/readFunnelStatusAggregated",
    params
  );
}

/** POST /api/v1/b4/readFunnelPerformanceAggregated — Funnel-Performance (Sprint 6b). */
export async function readFunnelPerformanceAggregated(
  params: { range?: string; start_date?: string; end_date?: string } = {}
): Promise<FunnelPerformanceResult> {
  return await callBackend<FunnelPerformanceResult>(
    "/api/v1/b4/readFunnelPerformanceAggregated",
    params
  );
}

/** POST /api/v1/b4/readVertriebUebersichtAggregated — Vertriebs-Übersicht (Sprint 7a). */
export async function readVertriebUebersichtAggregated(
  params: { typ?: string } = {}
): Promise<VertriebUebersichtResult> {
  return await callBackend<VertriebUebersichtResult>(
    "/api/v1/b4/readVertriebUebersichtAggregated",
    params
  );
}

/** POST /api/v1/b1/readZieleAggregated — Ziele-View (Sprint 7b). */
export async function readZieleAggregated(
  params: { iso_jahr?: number; iso_woche?: number } = {}
): Promise<ZieleResult> {
  return await callBackend<ZieleResult>("/api/v1/b1/readZieleAggregated", params);
}

/** POST /api/v1/b4/writeBereichsziel — BL/Admin Bereichsziel setzen (Sprint 7b). */
export async function writeBereichsziel(
  payload: { wert: number; von?: string; stichtag: string }
): Promise<{ saved: boolean }> {
  return await callBackend<{ saved: boolean }>("/api/v1/b4/writeBereichsziel", payload);
}

/** POST /api/v1/b1/writeWochenSoll — Wochen-Soll setzen (Sprint 7b reuse). */
export async function writeWochenSoll(payload: {
  person_id: string; iso_jahr: number; iso_woche: number;
  soll_pg: number; soll_mt: number;
}): Promise<unknown> {
  return await callBackend("/api/v1/b1/writeWochenSoll", payload);
}

/**
 * POST /api/v1/auth/logout. callBackend würde Auto-Logout bei 401 triggern;
 * Logout selbst will das nicht (User hat aktiv geklickt). Daher direkt
 * mit fetch + clearToken nach Erfolg.
 */
export async function logoutBackend(): Promise<LogoutResult | null> {
  const token = getToken();
  if (!token) {
    return null;
  }

  try {
    const res = await fetch(`${WORKER_URL}/api/v1/auth/logout`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) return null;
    const data = (await res.json()) as ApiResponseOk<LogoutResult>;
    return data.status === "ok" ? data.result : null;
  } catch (err) {
    console.warn(`[logout] Backend-Call failed: ${(err as Error).message}`);
    return null;
  } finally {
    clearToken();
  }
}

// -----------------------------------------------------------------------------
// Profil-Bild (Phase 3.6) — Endpoints liegen unter /profile/* (NICHT /api/v1/*)
// -----------------------------------------------------------------------------

/**
 * POST /profile/upload — sendet den (client-seitig auf 256×256 resized) Blob als
 * rohen Body. Returnt das neue profilbild_key-Token (Cache-Bust + Existenz-Flag).
 */
export async function uploadProfileImage(blob: Blob): Promise<{ profilbild_key: string; person_id: string }> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/profile/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": blob.type || "image/webp" },
      body: blob
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Upload nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  const data = (await res.json()) as ApiResponseOk<{ profilbild_key: string; person_id: string }> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Upload fehlgeschlagen", statusCode: res.status });
  }
  return data.result;
}

/** DELETE /profile/image — löscht das eigene Profilbild (R2 + Sheet-Token). */
export async function deleteProfileImage(): Promise<void> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/profile/image`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Delete nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  const data = (await res.json()) as ApiResponseOk<unknown> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Löschen fehlgeschlagen", statusCode: res.status });
  }
}

/**
 * Bild-URL für <img src> (public GET). `cacheBust` = profilbild_key (Cache-Bust,
 * NICHT das JWT). Leerer Wert → kein Bild (Aufrufer zeigt Initialen).
 */
export function profileImageUrl(personId: string, cacheBust: string): string {
  return `${WORKER_URL}/profile/image/${encodeURIComponent(personId)}?v=${encodeURIComponent(cacheBust)}`;
}

// -----------------------------------------------------------------------------
// Vereins-Bild (KB „Profilbild" für Vereine) — Endpoints unter /kb-image/*
// -----------------------------------------------------------------------------

/** POST /kb-image/upload?id= — sendet den (256×256-resized) Blob als rohen Body. */
export async function uploadKbImage(companyId: string, blob: Blob): Promise<{ logo_key: string; company_id: string }> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/kb-image/upload?id=${encodeURIComponent(companyId)}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": blob.type || "image/webp" },
      body: blob
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Upload nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  const data = (await res.json()) as ApiResponseOk<{ logo_key: string; company_id: string }> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Upload fehlgeschlagen", statusCode: res.status });
  }
  return data.result;
}

/** DELETE /kb-image?id= — löscht das Vereins-Bild (R2 + VEREINE-Token). */
export async function deleteKbImage(companyId: string): Promise<void> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/kb-image?id=${encodeURIComponent(companyId)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Delete nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  const data = (await res.json()) as ApiResponseOk<unknown> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Löschen fehlgeschlagen", statusCode: res.status });
  }
}

/**
 * Bild-URL für das Vereins-Bild (auth-gated GET → via fetch+Blob laden).
 * `cacheBust` = logo_key (Cache-Bust, NICHT das JWT).
 */
export function kbImageUrl(companyId: string, cacheBust: string): string {
  return `${WORKER_URL}/kb-image/${encodeURIComponent(companyId)}?v=${encodeURIComponent(cacheBust)}`;
}

// -----------------------------------------------------------------------------
// Admin — MA-Verwaltung (Phase 3.7.2). Endpoints unter /api/v1/admin/ma/*.
// -----------------------------------------------------------------------------

export type MaCreateInput = {
  vorname: string; nachname: string; email: string;
  kuerzel?: string; farbe?: string; vertragsbeginn?: string;
  email_alt?: string; hs_owner_id?: string; notiz?: string; bereiche: string[];
  typ?: "regular" | "praktikant"; // Phase 3.7.8
  slack_user_id?: string; // Phase 3.9 (HubSpot-ID = bestehendes hs_owner_id)
  bei_cotrainer_seit?: string; // Feature A — Eintrittsdatum YYYY-MM-DD (vertragsunabhängig)
  personalnummer?: string; // Payroll-/DATEV-Pers.-Nr. — Mapping auf Abrechnungen, NICHT die person_id
};
export type MaUpdateInput = { person_id: string } & Partial<Omit<MaCreateInput, "bereiche">> & {
  bereiche?: string[];
  vertragsende?: string;
  /** W1-SE-3 — Stammdaten-Spalte `status` (nicht der abgeleitete Anzeige-Status). */
  status_feld?: string;
  /** W1-SE-3 — Anzeige-Reihenfolge. Weglassen = unverändert. */
  sort_order?: number;
};

export async function adminMaList(): Promise<MaListResult> {
  return await callBackend<MaListResult>("/api/v1/admin/ma/list", {});
}
export async function adminMaCreate(input: MaCreateInput): Promise<{ person_id: string; bereiche: string[]; typ?: "regular" | "praktikant"; invite?: "sent" | "failed" }> {
  return await callBackend("/api/v1/admin/ma/create", input as Record<string, unknown>);
}
/** Onboarding-Mail erneut senden (3.7.7). */
export async function adminMaResendInvite(personId: string): Promise<{ person_id: string; email: string; invite: "sent" | "failed"; error?: string }> {
  return await callBackend("/api/v1/admin/ma/resend-invite", { person_id: personId });
}
export async function adminMaUpdate(input: MaUpdateInput): Promise<{ person_id: string; updated: string[] }> {
  return await callBackend("/api/v1/admin/ma/update", input as Record<string, unknown>);
}
/** Onboarding-Provisioning Stufe 1: Slack/HubSpot-Account per E-Mail auflösen + ID zurückschreiben. */
export type MaProvisionResult = {
  person_id: string; email: string;
  slack?: { found: boolean; id?: string; error?: string };
  hubspot?: { found: boolean; id?: string; error?: string };
};
export async function adminMaProvision(personId: string, services: Array<"slack" | "hubspot">): Promise<MaProvisionResult> {
  return await callBackend("/api/v1/admin/ma/provision", { person_id: personId, services });
}
/**
 * Deaktivieren mit optionalem Vertragsende (Phase 3.7.3 §5): leer = sofort,
 * Zukunft = geplant (MA bleibt aktiv bis X, Gate sperrt automatisch).
 */
export async function adminMaDeactivate(
  personId: string,
  vertragsende?: string
): Promise<{ person_id: string; aktiv: boolean; vertragsende: string; scheduled: boolean }> {
  return await callBackend("/api/v1/admin/ma/deactivate", {
    person_id: personId,
    ...(vertragsende !== undefined ? { vertragsende } : {})
  });
}
export async function adminMaReactivate(personId: string): Promise<{ person_id: string; aktiv: boolean }> {
  return await callBackend("/api/v1/admin/ma/reactivate", { person_id: personId });
}

/** Rollen einer Email setzen (CT_Berechtigungen reconcilen) — Phase 3.7.3. */
export async function adminBerechtigungSet(input: {
  email: string;
  person_id?: string; // für target_person_id-Audit (Phase 3.7.3 §9)
  bl_bereiche: string[];
  /**
   * 🆕 03.09. — Betreuer-Rolle (`kundenbetreuer`) je Bereich.
   * 🔴 **Weglassen heißt „nicht anfassen", `[]` heißt „keine".** Absicht: der
   * HR-Schirm ruft denselben Endpunkt ohne dieses Feld auf und darf niemandem
   * still die Rolle entziehen.
   */
  betreuer_bereiche?: string[];
  is_admin: boolean;
}): Promise<{ email: string; bl_bereiche: string[]; betreuer_bereiche: string[]; is_admin: boolean; added: number; removed: number }> {
  return await callBackend("/api/v1/admin/berechtigung/set", input as Record<string, unknown>);
}

// Lost-Grund-Codes (Phase 7.4) — Admin-Editor. Canonical = B2 _LOST_GRUND_CODES.
// V19: usage_90d = Vorkommen des Codes in den letzten 90 Tagen.
export type LostGrundCodeItem = { code: string; label: string; usage_90d?: number };
export async function adminLostGrundList(): Promise<{ codes: LostGrundCodeItem[] }> {
  return await callBackend("/api/v1/admin/lost-grund-codes/list", {});
}
export async function adminLostGrundUpsert(code: string, label: string): Promise<{ code: string; label: string; action: "updated" | "created" }> {
  return await callBackend("/api/v1/admin/lost-grund-codes/upsert", { code, label });
}
export async function adminLostGrundDelete(code: string): Promise<{ code: string; deleted: boolean }> {
  return await callBackend("/api/v1/admin/lost-grund-codes/delete", { code });
}

// Vertrieb-Aktiv-Roster (Phase 7.4) — Fenster-Editor (Caller/Host). „host" → MT+FU.
export type RosterFenster = { row: number; von: string; bis: string };
export type RosterPersonItem = {
  person_id: string;
  anzeigename: string;
  email: string;
  farbe?: string;
  profilbild_key?: string;
  caller: RosterFenster[];
  host: RosterFenster[];
  host_sync_drift: boolean;
};
export async function rosterList(): Promise<{ roster: RosterPersonItem[] }> {
  return await callBackend("/api/v1/admin/vertrieb-roster/list", {});
}
export async function rosterAdd(person_id: string, funktion: "caller" | "host", von: string, bis: string): Promise<{ tabs: string[] }> {
  return await callBackend("/api/v1/admin/vertrieb-roster/add", { person_id, funktion, von, bis });
}
export async function rosterDelete(person_id: string, funktion: "caller" | "host", von: string, bis: string): Promise<{ deleted: string[] }> {
  return await callBackend("/api/v1/admin/vertrieb-roster/delete", { person_id, funktion, von, bis });
}

// Slot-Verfügbarkeit (Host_MT_Verfuegbarkeit) — Phase 7.4 Item 3.
export type VerfuegEintragItem = {
  id: string; row: number; wochentag: number; uhrzeit: string;
  gueltig_von: string; gueltig_bis: string; notiz: string; wochen_im_monat: string;
};
export type VerfuegHostItem = { host_person_id: string; anzeigename: string; eintraege: VerfuegEintragItem[] };
export type VerfuegInputPayload = {
  person_id: string; wochentag: number; uhrzeit: string;
  gueltig_von: string; gueltig_bis: string; notiz: string; wochen_im_monat: string; prev_id?: string;
};
export async function slotVerfuegbarkeitList(): Promise<{ hosts: VerfuegHostItem[] }> {
  return await callBackend("/api/v1/admin/slot-verfuegbarkeit/list", {});
}
export async function slotVerfuegbarkeitUpsert(input: VerfuegInputPayload): Promise<{ id: string; action: "added" | "updated" }> {
  return await callBackend("/api/v1/admin/slot-verfuegbarkeit/upsert", input as unknown as Record<string, unknown>);
}
export async function slotVerfuegbarkeitDelete(id: string): Promise<{ deleted: boolean }> {
  return await callBackend("/api/v1/admin/slot-verfuegbarkeit/delete", { id });
}
export type SlotBulkCell = { wochentag: number; uhrzeit: string };
export async function slotVerfuegbarkeitBulkSet(input: {
  person_id: string; gueltig_von: string; gueltig_bis: string; notiz: string; wochen_im_monat: string; cells: SlotBulkCell[];
}): Promise<{ added: number; deleted: number }> {
  return await callBackend("/api/v1/admin/slot-verfuegbarkeit/bulk-set", input as unknown as Record<string, unknown>);
}

// Rotations-Register (wer teilt sich einen Wochentag reihum) — Justin 2026-07-27.
export type RotationsTeilnehmerItem = {
  person_id: string; anzeigename: string; rota_key: number;
  uhrzeiten: string[]; gueltig_von: string; gueltig_bis: string;
};
export type RotationsKandidatItem = { person_id: string; anzeigename: string };
export type RotationsVorschauItem = { datum: string; person_id: string; anzeigename: string };
export type SlotRotationResult = {
  wochentag: number;
  teilnehmer: RotationsTeilnehmerItem[];
  kandidaten: RotationsKandidatItem[];
  vorschau: RotationsVorschauItem[];
  default_times: string[];
};
export async function slotRotationList(wochentag: number): Promise<SlotRotationResult> {
  return await callBackend("/api/v1/admin/slot-rotation/list", { wochentag });
}
export async function slotRotationSet(input: {
  person_id: string; wochentag: number; rota_key: number; uhrzeiten: string[]; gueltig_von: string;
}): Promise<{ angelegt: number; ersetzt: number; beendet: number }> {
  return await callBackend("/api/v1/admin/slot-rotation/set", input as unknown as Record<string, unknown>);
}
export async function slotRotationRemove(person_id: string, wochentag: number, ab: string): Promise<{ beendet: number; entfernt: number }> {
  return await callBackend("/api/v1/admin/slot-rotation/remove", { person_id, wochentag, ab });
}

// Vertrieb-Schwellen (Pflegelücken T5/T6/T7 + FU-Inaktiv) — Phase 7.4 Item 4.
export type VertriebThresholds = { t5: number; t6: number; t7: number; inaktiv_tage: number };
export async function vertriebThresholdsGet(): Promise<{ thresholds: VertriebThresholds; defaults: VertriebThresholds }> {
  return await callBackend("/api/v1/admin/vertrieb-thresholds/get", {});
}
export async function vertriebThresholdsSet(t: VertriebThresholds): Promise<{ thresholds: VertriebThresholds }> {
  return await callBackend("/api/v1/admin/vertrieb-thresholds/set", t as unknown as Record<string, unknown>);
}

// System-Settings (Admin) — Phase 7.4 Item 6+.
export type SystemSettings = {
  date_format: "de" | "iso";
  /** CSV der Workspace-Domains für Profil-Selbstzuordnung (SSO). Leer = aus. */
  workspace_sso_domains: string;
  /** CSV der Rollen, die bei Selbstzuordnung Admin-Freigabe erfordern. Leer = "admin". */
  profile_claim_sensitive_roles: string;
  /** R7 — Vertriebs-Liga live (true) oder Vorschau nur BL/Admin (false). */
  liga_live: boolean;
  /** Ausbaustufe 4 — Arena live (true) oder Vorschau nur BL/Admin (false). */
  arena_live: boolean;
};
export async function systemSettingsGet(): Promise<{ settings: SystemSettings; defaults: SystemSettings }> {
  return await callBackend("/api/v1/admin/system-settings/get", {});
}
/** Teil-Update möglich: nur gesetzte Felder werden überschrieben (Backend merged). */
export async function systemSettingsSet(s: Partial<SystemSettings>): Promise<{ settings: SystemSettings }> {
  return await callBackend("/api/v1/admin/system-settings/set", s as unknown as Record<string, unknown>);
}

// Feiertage / Sonder-Schließtage (System, Admin) — Phase 7.4 Item 7.
// typ (feiertag|brueckentag|betriebsferien|schliesstag) + aktiv-Flag (Toggle) + Seed.
// scope: "bundesweit" oder Bundesland-Kürzel (Geltungsbereich gesetzlicher Feiertage).
export type FeiertagTyp = "feiertag" | "brueckentag" | "betriebsferien" | "schliesstag";
export type FeiertagScope =
  | "bundesweit"
  | "BW" | "BY" | "BE" | "BB" | "HB" | "HH" | "HE" | "MV"
  | "NI" | "NRW" | "RP" | "SL" | "SN" | "ST" | "SH" | "TH";
export type FeiertagBereich = "unternehmensweit" | "vertrieb";
export type EffectiveFeiertagKind = "gesetzlich-bundesweit" | "gesetzlich-regional" | "manuell";
/** Effektiver Feiertag/Schließtag eines Jahres (computed ⊕ Overrides ⊕ manuell). */
export type FeiertagItem = {
  datum: string;
  bezeichnung: string;
  kind: EffectiveFeiertagKind;
  typ: FeiertagTyp;
  hard: boolean;
  aktiv: boolean;
  scope: FeiertagScope;
  bereich: FeiertagBereich;
  bundeslaender: FeiertagScope[];
};
/** Effektive Liste eines Jahres (kein Seed mehr — Default algorithmisch). */
export async function feiertageList(year?: number): Promise<{ year: number; feiertage: FeiertagItem[] }> {
  return await callBackend("/api/v1/admin/feiertage/list", year !== undefined ? { year } : {});
}
/** Manuellen Schließtag anlegen/aktualisieren (mit Bereich). */
export async function feiertageUpsert(
  datum: string,
  bezeichnung: string,
  opts: { typ?: FeiertagTyp; aktiv?: boolean; scope?: FeiertagScope; bereich?: FeiertagBereich } = {}
): Promise<{ datum: string; action: "added" | "updated" }> {
  return await callBackend("/api/v1/admin/feiertage/upsert", {
    datum,
    bezeichnung,
    ...(opts.typ !== undefined ? { typ: opts.typ } : {}),
    ...(opts.aktiv !== undefined ? { aktiv: opts.aktiv } : {}),
    ...(opts.scope !== undefined ? { scope: opts.scope } : {}),
    ...(opts.bereich !== undefined ? { bereich: opts.bereich } : {})
  });
}
/**
 * Toggle. Gesetzliche Feiertage (gesetzlich=true, Default) → Override
 * anlegen/entfernen (datum-keyed, bezeichnung für die Anlage). Manuelle
 * Schließtage (gesetzlich=false) → bestehende Zeile togglen (scope-genau).
 */
export async function feiertageToggle(
  datum: string,
  aktiv: boolean,
  opts: { gesetzlich?: boolean; bezeichnung?: string; scope?: FeiertagScope } = {}
): Promise<{ datum: string; aktiv: boolean; action: string }> {
  return await callBackend("/api/v1/admin/feiertage/toggle", {
    datum,
    aktiv,
    ...(opts.gesetzlich !== undefined ? { gesetzlich: opts.gesetzlich } : {}),
    ...(opts.bezeichnung !== undefined ? { bezeichnung: opts.bezeichnung } : {}),
    ...(opts.scope !== undefined ? { scope: opts.scope } : {})
  });
}
export async function feiertageDelete(datum: string, scope?: FeiertagScope): Promise<{ deleted: boolean }> {
  return await callBackend("/api/v1/admin/feiertage/delete", {
    datum,
    ...(scope !== undefined ? { scope } : {})
  });
}

// Referenz-Bezeichnungen (Lead-Status + Outcomes) — Phase 7.4 Item 8.
// V17: `usage` (number) pro Eintrag. V14/V15: für leadstatus zusätzlich
// `hs` (HubSpot-Stage) + `phase` — Worker liefert beide auch in `extra`.
export type RefEntryItem = {
  id: string;
  label: string;
  extra: Record<string, string>;
  row: number;
  /** V17 — Verwendungs-Zähler (Lead-Status = #Companies aktuell, Outcomes = #Vorkommen). */
  usage?: number;
  /** V14/V15 — HubSpot-Pipeline-Stage (nur leadstatus, spiegelt extra.hs). */
  hs?: string;
  /** V16 — Phase (nur leadstatus, spiegelt extra.phase). */
  phase?: string;
};
export type ReferenzenResult = { leadstatus: RefEntryItem[]; meeting_outcome: RefEntryItem[]; fu_outcome: RefEntryItem[] };
export async function referenzenList(): Promise<{ referenzen: ReferenzenResult }> {
  return await callBackend("/api/v1/admin/referenzen/list", {});
}
export async function referenzenSetLabel(ref_type: "leadstatus" | "meeting_outcome" | "fu_outcome", id: string, label: string): Promise<{ ref_type: string; id: string; label: string }> {
  return await callBackend("/api/v1/admin/referenzen/set-label", { ref_type, id, label });
}
/** V14/V15 — HubSpot-Stage (hs) und/oder Phase eines Lead-Status setzen. Wirkt beim nächsten M17b-Snapshot. */
export async function referenzenSetMapping(input: { id: string; hs?: string; phase?: string }): Promise<{ id: string; hs?: string; phase?: string }> {
  return await callBackend("/api/v1/admin/referenzen/set-mapping", input as Record<string, unknown>);
}

// Companies-Cache (Snapshot M17b) — Status + manueller Refresh. Phase 7.4 Item 9.
export type CompaniesCacheStatus = { snapshot_rows: number; snapshot_last: string; cache_rows: number; cadence: string };
export async function companiesCacheStatus(): Promise<CompaniesCacheStatus> {
  return await callBackend("/api/v1/admin/companies-cache/status", {});
}
export type CompaniesSnapshotRunResult = { rows: number; target: string; diff?: { rows_live: number; rows_shadow: number; matched: number; mismatch_count: number } };
export async function companiesSnapshotRun(shadow: boolean): Promise<CompaniesSnapshotRunResult> {
  return await callBackend(`/api/v1/admin/cron/companies-snapshot/run?shadow=${shadow ? "true" : "false"}`, {});
}

// Companies-Unmatched (Karte A7/A8) — Vereine ohne HubSpot-Match + manuelle Zuordnung. Admin-only.
export type UnmatchedVereinItem = { verein: string; quelle: string; seit: string };
export async function companiesUnmatchedList(): Promise<{ vereine: UnmatchedVereinItem[] }> {
  return await callBackend("/api/v1/admin/companies/unmatched/list", {});
}

// Vereins-Stammdaten (D15 #6) — alle bekannten Verein↔Company-Einträge (read-only).
export type CompanyStammdatenItem = { verein: string; company_id: string; name: string; method: string; matched_at: string; matched: boolean };
export async function companiesCacheList(): Promise<{ eintraege: CompanyStammdatenItem[] }> {
  return await callBackend("/api/v1/admin/companies/list", {});
}
export type HsCompanyHit = { id: string; name: string; lead_status: string; lifecycle: string; vertriebler: string };
export async function companiesHubspotSearch(query: string): Promise<{ query: string; companies: HsCompanyHit[] }> {
  return await callBackend("/api/v1/admin/companies/hubspot-search", { query });
}
export async function companiesAssign(verein: string, companyId: string): Promise<{ verein: string; company_id: string; company_name: string; action: string }> {
  return await callBackend("/api/v1/admin/companies/assign", { verein, company_id: companyId });
}

// Integrations-Cockpit (read-only Health) — Phase 7.4 Item 10.
export type IntegrationStatusItem = { key: string; name: string; configured: boolean; detail: string };
export type CronItem = { schedule: string; description: string };
export async function integrationsStatus(): Promise<{ integrations: IntegrationStatusItem[]; crons: CronItem[] }> {
  return await callBackend("/api/v1/admin/integrations/status", {});
}

// Gate-Cockpit (R7 WP5) — read-only Ist-Zustand aller Feature-/Cutover-Gates. Admin-only.
export type GateItem = { key: string; value: string; note?: string };
export type GateBlock = { key: string; title: string; pflege: string; items: GateItem[]; error?: string };
export async function adminGates(): Promise<{ blocks: GateBlock[] }> {
  return await callBackend("/api/v1/admin/gates", {});
}

// Cron-An/Aus-Toggle (Integrations-Cockpit). Schedule bleibt deploy-fix; der
// Toggle steuert nur, ob der Worker den Job ausführt.
export type CronListItem = {
  key: string;
  cron: string;
  description: string;
  schedule_human: string;
  enabled: boolean;
};
export async function cronsList(): Promise<{ crons: CronListItem[] }> {
  return await callBackend("/api/v1/admin/crons/list", {});
}
export async function cronToggle(key: string, enabled: boolean): Promise<{ key: string; enabled: boolean }> {
  return await callBackend("/api/v1/admin/crons/toggle", { key, enabled });
}

/**
 * EINEN Cron sofort ausfuehren (Admin-only).
 *
 * WARNUNG: derselbe Runner wie im Scheduler — Slack geht raus, Tabellen werden
 * geschrieben. Die Oberflaeche verlangt deshalb einen zweiten Klick. Die Antwort
 * traegt die Stats des Laufs, damit sichtbar ist, was passiert ist.
 */
export type CronRunResult = {
  key: string;
  dauer_ms: number;
  toggle_enabled: boolean;
  stats: unknown;
};
/**
 * "Mache ich trotzdem" — einen Termin trotz eigener Sperrzeit bestaetigen
 * (Justin 17.09.2026). Danach verschwindet die Marke SPERRZEIT an dieser Zeile.
 *
 * Der Schluessel ist das PAAR aus Termin und Sperrzeit: aendert jemand seine
 * Grenze, entsteht eine neue sperrzeit_id und die Bestaetigung verfaellt.
 */
export async function sperrzeitBestaetigen(
  meetingId: string, sperrzeitId: string, zuruecknehmen = false
): Promise<{ meeting_id: string; bestaetigt: boolean }> {
  return await callBackend("/api/v1/b2/sperrzeitBestaetigen", {
    meeting_id: meetingId, sperrzeit_id: sperrzeitId, zuruecknehmen
  });
}

export async function cronRun(key: string): Promise<CronRunResult> {
  return await callBackend("/api/v1/admin/crons/run", { key });
}

/**
 * Automatik-Schalter (Justin 16.09.2026) — die Modus-Umschalter, die
 * entscheiden, ob eine Automatik meldet oder schreibt.
 *
 * 📏 Bis zum 16.09. hatte KEINER der fünf Modus-Schalter eine Oberfläche; sie
 * lagen nur in D1. Aufgefallen ist es, als Justin einen davon suchte.
 */
export type AutomationSchalter = {
  automation: string;
  key: string;
  klarname: string;
  erklaerung: string;
  werte: Array<{ wert: string; klarname: string; wirkung: string }>;
  fallback: string;
  aktuell: string;
  /** 🔑 false = der Wert steht NICHT in der Datenbank, es gilt der Fallback.
   *  „aus, weil niemand ihn gesetzt hat" ist etwas anderes als „aus, weil
   *  jemand ihn so gesetzt hat" — die Anzeige muss das unterscheiden können. */
  gesetzt: boolean;
};

export async function schalterList(): Promise<{ schalter: AutomationSchalter[] }> {
  return await callBackend("/api/v1/admin/schalter/list", {});
}

export async function schalterSet(key: string, wert: string): Promise<{ key: string; wert: string; vorher: string }> {
  return await callBackend("/api/v1/admin/schalter/set", { key, wert });
}

// Vertriebshistorie-Manual-Trigger (E2-Backlog → E11). Endpoint existiert seit
// Phase 3.12 B, hatte im Cockpit aber keinen Bedienweg. `dry_run` DEFAULT true —
// nur ein explizites `false` schreibt 10_VEREINE + 20_EVENTS (modeOverride=live,
// überstimmt VERTRIEBSHISTORIE_MODE=off).
export type VhRunResult = {
  mode: string;
  vereine: number;
  events: number;
  duration_ms: number;
  skipped?: boolean;
  error?: string;
  manual: boolean;
};
export async function cronVertriebshistorieRun(dryRun: boolean): Promise<VhRunResult> {
  return await callBackend(`/api/v1/admin/cron/vertriebshistorie/run?dry_run=${dryRun ? "true" : "false"}`, {});
}

/** Funktions-Rollen (Caller/Host) im Bereich Vertrieb setzen — Phase 3.7.3 §3. */
export async function adminRolleSet(input: {
  person_id: string;
  vertrieb?: { caller?: boolean; host?: boolean };
  /**
   * 🆕 02.09. — Vereinseinrichter/Trainerfortbilder im Bereich **`kb`**.
   * 🔴 03.09. umbenannt von `kundenbetreuung`: der Bereich heißt im Admin und im
   * Backend-Enum `kb`, und derselbe Zweitname hatte im Frontend die Pill-Reihe
   * unsichtbar gemacht. 🔑 *Ein Bereich, ein Name.*
   */
  kb?: { vereinseinrichter?: boolean; trainerfortbilder?: boolean };
}): Promise<{ person_id: string; vertrieb: unknown; result: unknown; kb: unknown }> {
  return await callBackend("/api/v1/admin/rolle/set", input as Record<string, unknown>);
}

export type MaDetailResult = {
  stammdaten: {
    person_id: string; vollname: string; email: string; aktiv: boolean;
    vertragsbeginn: string; vertragsende: string; bereiche: string[]; status: string;
    /** Vertrag: raw YYYY-MM-DD fürs Edit-Feld in der Detailsicht. */
    vertragsbeginn_raw?: string; vertragsende_raw?: string;
    /** Phase 3.7.8 */
    typ?: "regular" | "praktikant";
    /** Phase 3.9 — Slack-User-ID (Z) + HubSpot-Owner-ID (H/hs_owner_id), sichtbar/editierbar. */
    slack_user_id?: string;
    hs_owner_id?: string;
    /** Feature A — Eintrittsdatum: DE-formatiert (Anzeige) + raw YYYY-MM-DD (Edit-Modal). */
    bei_cotrainer_seit?: string;
    bei_cotrainer_seit_raw?: string;
    /** Payroll-/DATEV-Pers.-Nr. — Mapping auf Abrechnungen und Lohnunterlagen. */
    personalnummer?: string;
    /**
     * W1-SE-3 — die Stammdaten-SPALTE `status` ("aktiv" / "Ex-MA" / leer).
     * ⚠️ NICHT das abgeleitete `status` oben („aktiv (endet …)").
     */
    status_feld?: string;
    /** W1-SE-3 — Anzeige-Reihenfolge (D1-only, bisher nur per SQL änderbar). */
    sort_order?: number | null;
  };
  aktuelle_rollen: {
    funktionen: Array<{ funktion: string; seit: string }>;
    berechtigungen: { bl_bereiche: string[]; is_admin: boolean };
    /**
     * 🆕 02.09. — die LAUFENDEN VE/TF-Rollen als Terminarten.
     * ⚠️ null heisst NICHT LESBAR, [] heisst KEINE. Das Modal darf nur im
     * zweiten Fall ein Haekchen entfernen.
     */
    kb_rollen: string[] | null;
    /** Die Geschichte daneben — wer war von wann bis wann in der Rolle. */
    kb_historie?: Array<{ terminart: string; aktivVon: string; aktivBis: string }>;
  };
  timeline: Array<{ datum: string; ts: number; typ: string; beschreibung: string; durch: string }>;
};

/** GET /api/v1/admin/ma/:personId/detail — Detail-View (Phase 3.7.3 §9). GET-Workaround wie getUserContext. */
export async function adminMaDetail(personId: string): Promise<MaDetailResult> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/admin/ma/${encodeURIComponent(personId)}/detail`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Backend nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  if (res.status === 401) {
    clearToken();
    redirectToLogin();
    throw new ApiError({ code: "AUTH_EXPIRED", message: "Session abgelaufen", statusCode: 401 });
  }
  const data = (await res.json()) as ApiResponseOk<MaDetailResult> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Detail-Fehler", statusCode: res.status });
  }
  return data.result;
}

// -----------------------------------------------------------------------------
// Phase 3.7.6 — Audit-Log-View (D1 read-only). GET-Workaround mit Query-Params.
// -----------------------------------------------------------------------------

export type AuditEventItem = {
  id: number;
  auditId: string;
  timestamp: number;
  sheetKey: string;
  action: string;
  userEmail: string;
  userPersonId: string | null;
  targetPersonId: string | null;
  contextSource: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};
export type AuditQueryParams = { user?: string; action?: string; from?: string; to?: string; limit?: number; offset?: number };
export type AuditQueryResult = { events: AuditEventItem[]; limit: number; offset: number; has_more: boolean };

/** GET /api/v1/admin/audit (admin-or-bl, read-only). GET-Workaround wie adminMaDetail. */
export async function adminAuditQuery(params: AuditQueryParams = {}): Promise<AuditQueryResult> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  const qs = new URLSearchParams();
  if (params.user) qs.set("user", params.user);
  if (params.action) qs.set("action", params.action);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/admin/audit?${qs.toString()}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Backend nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  if (res.status === 401) {
    clearToken();
    redirectToLogin();
    throw new ApiError({ code: "AUTH_EXPIRED", message: "Session abgelaufen", statusCode: 401 });
  }
  const data = (await res.json()) as ApiResponseOk<AuditQueryResult> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Audit-Fehler", statusCode: res.status });
  }
  return data.result;
}

// -----------------------------------------------------------------------------
// Phase 3.7.4 — System-Settings (CT_Settings Key-Value-Editor)
// -----------------------------------------------------------------------------

export type SettingItem = {
  key: string;
  value: string | null; // null bei sensiblen Keys (maskiert)
  hint?: string; // ••••last4 / (nicht gesetzt) — nur bei sensitive
  description?: string;
  section?: string; // Block-Marker (UI-Gruppierung)
  sensitive: boolean;
  critical: boolean;
};
export type SettingsListResult = { settings: SettingItem[]; sections: string[] };

/** GET /api/v1/admin/settings (GET-Workaround wie getUserContext). */
export async function adminSettingsList(): Promise<SettingsListResult> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/admin/settings`, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Backend nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  if (res.status === 401) {
    clearToken();
    redirectToLogin();
    throw new ApiError({ code: "AUTH_EXPIRED", message: "Session abgelaufen", statusCode: 401 });
  }
  const data = (await res.json()) as ApiResponseOk<SettingsListResult> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Settings-Fehler", statusCode: res.status });
  }
  return data.result;
}

export async function adminSettingsSet(
  key: string,
  value: string
): Promise<{ key: string; updated: boolean; sensitive: boolean; critical: boolean }> {
  return await callBackend("/api/v1/admin/settings/set", { key, value });
}

/**
 * W1-SE-4 — NEUEN ct-Key anlegen. Getrennt von `adminSettingsSet`, weil „anlegen"
 * eine Beschreibung verlangt und bei vorhandenem Key mit CONFLICT antwortet
 * (statt still zu überschreiben).
 */
export async function adminSettingsCreate(
  key: string,
  value: string,
  beschreibung: string,
  section?: string
): Promise<{ key: string; created: boolean }> {
  return await callBackend("/api/v1/admin/settings/create", { key, value, beschreibung, section });
}

// -----------------------------------------------------------------------------
// Phase 3.7.5 — Sheet-Inventar + Health-Check (admin-only, read-only)
// -----------------------------------------------------------------------------

export type SheetInventoryItem = {
  bereich: string; sheet_key: string; sheet_name: string;
  spreadsheet_id: string; script_id: string; phase: string; status: string; notiz: string;
};
export type SheetsListResult = { sheets: SheetInventoryItem[] };

export type SheetHealthItem = {
  sheet_key: string;
  level: "ok" | "warn" | "error" | "skipped";
  reachable: boolean;
  http_status: number | null;
  tab_count: number;
  tabs: Array<{ title: string; rows: number; cols: number }>;
  message: string;
};
export type SheetsHealthResult = {
  health: SheetHealthItem[];
  summary: { total: number; ok: number; warn: number; error: number; skipped: number };
};

/** Kleiner GET-Helper (Bearer + 401-Redirect + Envelope-Auspacken). */
async function backendGetJson<T>(path: string, errLabel: string): Promise<T> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}${path}`, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Backend nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  if (res.status === 401) {
    clearToken();
    redirectToLogin();
    throw new ApiError({ code: "AUTH_EXPIRED", message: "Session abgelaufen", statusCode: 401 });
  }
  const data = (await res.json()) as ApiResponseOk<T> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? errLabel, statusCode: res.status });
  }
  return data.result;
}

/** GET /api/v1/admin/sheets — Inventar (schnell). */
export async function adminSheetsList(): Promise<SheetsListResult> {
  return await backendGetJson<SheetsListResult>("/api/v1/admin/sheets", "Sheet-Inventar-Fehler");
}

/** GET /api/v1/admin/sheets/health — Health-Probe pro Sheet (langsamer). */
export async function adminSheetsHealth(): Promise<SheetsHealthResult> {
  return await backendGetJson<SheetsHealthResult>("/api/v1/admin/sheets/health", "Health-Check-Fehler");
}

// ---------------------------------------------------------------------------
// Automatik-Sicht (E12b-2) — das Register + die Lage je Automatik.
//
// 🔑 `beleg` ist das wichtigste Feld und KEIN Schmuck: 'nur_lauf_ende' heißt,
// dass ein Lauf-Ende vorliegt, aber KEIN Wirkungsnachweis. Alle Make-Läufe sind
// so — gemessen am 03.08.2026: Make gibt bei erfolgreichen Läufen keine
// Modul-Ebene her. Die Sicht darf so einen Lauf niemals grün zeichnen (Fall
// 7138428: zwei SUCCESS-Läufe, während HubSpot-Write, Datastore-Löschen und
// Mails NICHT ankamen).
// ---------------------------------------------------------------------------

export type AutomationLage = {
  automation_id: string;
  laeufe: number;
  ok: number;
  uebersprungen: number;
  fehler: number;
  letzter_lauf: string | null;
  letzter_ausgang: string | null;
  letzter_grund: string | null;
  /** Echte Fehlermeldung des jüngsten gescheiterten Laufs — der Ansatzpunkt. */
  fehler_text: string | null;
  /** Seit wann es hakt (erstes Auftreten im Fenster). */
  fehler_seit: string | null;
};

export type AutomationItem = {
  id: string;
  klasse: string;
  ort: string;
  /** Technischer Schlüssel — klein anzeigen, damit man im Code danach suchen kann. */
  name: string;
  /** Klarname für die Überschrift. */
  klarname: string;
  /** Ein Satz Klartext: was tut das für uns. */
  kurz: string;
  takt: string;
  /** Ausführliche technische Beschreibung (Detailansicht). */
  wirkt_auf: string;
  beleg: "wirkung" | "nur_lauf_ende";
  /**
   * Sammeln wir für diese Automatik überhaupt Läufe ein?
   * false ⇒ die Sicht zeigt „nicht erfasst", NICHT „still". Der Unterschied ist
   * nicht kosmetisch: „still" behauptet, sie sei nicht gelaufen.
   */
  erfasst: boolean;
  /**
   * 'abgeschaltet' ⇒ die Sicht zeigt 🧹 AUFRÄUMEN und zählt sie NICHT als
   * Defekt. Sie bleibt aber sichtbar: ein Register, das Erledigtes wegwirft,
   * kann „wurde das je abgeräumt?" nicht mehr beantworten.
   */
  zustand: "aktiv" | "abgeschaltet";
  abgeschaltet_am?: string;
  abgeschaltet_grund?: string;
  bereich: string;
  grenze?: string;
  lage: AutomationLage | null;
  stale: boolean | null;
  letzter_ok: string | null;
};

export type AutomationenResult = {
  fenster_stunden: number;
  zaehlung: Record<string, number>;
  automationen: AutomationItem[];
};

export type AutomationLaufZeile = {
  automation_id: string;
  klasse: string;
  ort: string;
  started_at: string;
  dauer_ms: number;
  ausgang: string;
  beleg: string;
  grund: string | null;
  getan: number | null;
  uebersprungen: number | null;
  fehler_text: string | null;
};

/** GET /api/v1/admin/automationen — Register + Lage im gewählten Zeitfenster. */
export async function adminAutomationen(stunden = 24): Promise<AutomationenResult> {
  return await backendGetJson<AutomationenResult>(
    `/api/v1/admin/automationen?stunden=${encodeURIComponent(String(stunden))}`,
    "Automatik-Übersicht-Fehler"
  );
}

/** GET /api/v1/admin/automationen/laeufe — Lauf-Historie EINER Automatik. */
export async function adminAutomationLaeufe(
  id: string,
  limit = 50
): Promise<{ automation: AutomationItem | null; laeufe: AutomationLaufZeile[] }> {
  return await backendGetJson(
    `/api/v1/admin/automationen/laeufe?id=${encodeURIComponent(id)}&limit=${limit}`,
    "Lauf-Historie-Fehler"
  );
}

/**
 * W1-SE-2 — Inventar-Eintrag anlegen/ändern (Upsert per `sheet_key`).
 * ⚠️ Ändert NICHT, welche Spreadsheets der Worker liest (das bleibt ENV) —
 * nur Inventar-Anzeige und Health-Check.
 */
export async function adminSheetsSave(entry: SheetInventoryItem): Promise<{ sheet_key: string; created: boolean }> {
  return await callBackend("/api/v1/admin/sheets/save", entry as unknown as Record<string, unknown>);
}

/** W1-SE-2 — Inventar-Eintrag entfernen. */
export async function adminSheetsDelete(sheetKey: string): Promise<{ sheet_key: string; deleted: boolean }> {
  return await callBackend("/api/v1/admin/sheets/delete", { sheet_key: sheetKey });
}

// -----------------------------------------------------------------------------
// Phase 3.9 C.2b / 3.11 — Slack-Templates-Editor
// -----------------------------------------------------------------------------

export type SlackTemplateItem = {
  context: string;
  description: string;
  template: string;
  active: boolean;
  is_default: boolean;
  // #32 — Pro-Context-Routing (Slack-Channel + Chat-Webhook).
  slack_channel: string;
  slack_on: boolean;
  chat_webhook: string;
  chat_on: boolean;
};
export type SlackTemplatesResult = {
  templates: SlackTemplateItem[];
  target: "justin_dm" | "vertriebs_channel" | "both";
  tab_exists: boolean;
  placeholders: string[];
};

/** GET /api/v1/admin/slack-templates (GET-Workaround). */
export async function adminSlackTemplatesList(): Promise<SlackTemplatesResult> {
  const token = getToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  }
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/admin/slack-templates`, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: `Backend nicht erreichbar: ${(err as Error).message}`, statusCode: 0 });
  }
  if (res.status === 401) {
    clearToken();
    redirectToLogin();
    throw new ApiError({ code: "AUTH_EXPIRED", message: "Session abgelaufen", statusCode: 401 });
  }
  const data = (await res.json()) as ApiResponseOk<SlackTemplatesResult> | ApiResponseError;
  if (data.status === "error") {
    throw new ApiError({ code: data.error.code, message: data.error.message ?? "Slack-Templates-Fehler", statusCode: res.status });
  }
  return data.result;
}

export type SlackTemplateRouting = {
  slack_channel?: string;
  slack_on?: boolean;
  chat_webhook?: string;
  chat_on?: boolean;
};
export async function adminSlackTemplatesSet(
  context: string,
  template: string,
  active: boolean,
  routing: SlackTemplateRouting = {}
): Promise<{ context: string; updated: boolean }> {
  return await callBackend("/api/v1/admin/slack-templates/set", {
    context,
    template,
    active,
    ...(routing.slack_channel !== undefined ? { slack_channel: routing.slack_channel } : {}),
    ...(routing.slack_on !== undefined ? { slack_on: routing.slack_on } : {}),
    ...(routing.chat_webhook !== undefined ? { chat_webhook: routing.chat_webhook } : {}),
    ...(routing.chat_on !== undefined ? { chat_on: routing.chat_on } : {})
  });
}

// =============================================================================
// Phase 3.12 — Vertriebshistorie (Read-only, GET-Endpoints mit Query-Params)
// =============================================================================

/**
 * Generischer GET-Wrapper analog `callBackend`, aber:
 *   - Methode = GET
 *   - Parameter via URL-Query-String (Auto-Serialisierung: Strings + Booleans)
 *   - Body leer
 * Auth + ApiError-Mapping + Auto-Logout-bei-401 identisch.
 */
async function getBackend<T = unknown>(
  endpoint: string,
  params: Record<string, string | number | boolean | undefined> = {},
  options: CallOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {};

  if (!options.noAuth) {
    const token = getToken();
    if (!token) {
      redirectToLogin();
      throw new ApiError({
        code: "NO_TOKEN",
        message: "Nicht eingeloggt — Redirect zum Login.",
        statusCode: 401
      });
    }
    headers["Authorization"] = `Bearer ${token}`;
  }

  // `new URL(relativ)` wirft ohne Basis — seit E11b ist WORKER_URL im Normalfall
  // leer (Proxy, same-origin). Deshalb window.location.origin als Basis.
  const url = new URL(`${WORKER_URL}${endpoint}`, window.location.origin);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    url.searchParams.set(k, String(v));
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), { method: "GET", headers });
  } catch (err) {
    throw new ApiError({
      code: "NETWORK_ERROR",
      message: `Backend nicht erreichbar: ${(err as Error).message}`,
      statusCode: 0
    });
  }

  if (res.status === 401 && !options.skipAutoLogout) {
    clearToken();
    redirectToLogin();
    throw new ApiError({
      code: "AUTH_EXPIRED",
      message: "Session abgelaufen — Redirect zum Login.",
      statusCode: 401
    });
  }

  let data: ApiResponseOk<T> | ApiResponseError;
  try {
    data = (await res.json()) as ApiResponseOk<T> | ApiResponseError;
  } catch {
    throw new ApiError({
      code: "INVALID_RESPONSE",
      message: `Server lieferte kein JSON (HTTP ${res.status})`,
      statusCode: res.status
    });
  }

  if (data.status === "error") {
    throw new ApiError({
      code: data.error.code,
      message: data.error.message ?? `Backend-Fehler ${data.error.code}`,
      statusCode: res.status,
      ...(data.error.audit_id !== undefined && { auditId: data.error.audit_id }),
      ...(data.error.field !== undefined && { field: data.error.field }),
      ...(data.error.issues !== undefined && { issues: data.error.issues }),
      ...(data.error.current_state !== undefined && { currentState: data.error.current_state })
    });
  }

  return data.result;
}

export type VertriebshistorieMasterRow = {
  company_id: string;
  verein: string;
  bereich_vertrieb: string;
  aktueller_lead_status: string;
  aktueller_lifecycle: string;
  status_seit: string;
  aktueller_zyklus_nr: number;
  zyklus_aktiv: boolean;
  gesamt_zyklen: number;
  erster_m1_datum: string;
  letzter_m1_datum: string;
  anzahl_m1: number;
  anzahl_m4_zusagen: number;
  anzahl_m5_no_show: number;
  anzahl_f1: number;
  anzahl_f9_zusagen: number;
  anzahl_closings: number;
  last_activity_datum: string;
  last_activity_typ: string;
  letzter_lost_grund: string;
  notiz: string;
};

export type VertriebshistorieEventRow = {
  event_id: string;
  company_id: string;
  verein: string;
  event_datum: string;
  event_typ: string;
  outcome_code: string;
  outcome_detail: string;
  zyklus_nr: number;
  quelle: string;
  quell_id: string;
  notiz: string;
};

export type VertriebshistorieListResult = {
  rows: VertriebshistorieMasterRow[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  filter: {
    q?: string;
    bereich?: string;
    status?: string;
    zyklus_aktiv?: boolean;
  };
};

export type VertriebshistoriePflegeRow = {
  source: "B2" | "B3" | "B4";
  typ: string;
  klartext: string;
  trigger: string;
  tage: number | null;
  owner: string;
};

export type VertriebshistorieDetailResult = {
  master: VertriebshistorieMasterRow | null;
  events: VertriebshistorieEventRow[];
  pflegeluecken: VertriebshistoriePflegeRow[];
};

/** GET /api/v1/vertriebshistorie — Master-Liste paginiert + filter (Phase 3.12 C.1). */
export async function readVertriebshistorieListe(params: {
  page?: number;
  page_size?: number;
  q?: string;
  bereich?: string;
  status?: string;
  zyklus_aktiv?: boolean;
} = {}): Promise<VertriebshistorieListResult> {
  return await getBackend<VertriebshistorieListResult>("/api/v1/vertriebshistorie", params);
}

/** GET /api/v1/vertriebshistorie/:companyId — Master + Timeline für 1 Verein (Phase 3.12 C.2). */
export async function readVertriebshistorieDetail(
  companyId: string
): Promise<VertriebshistorieDetailResult> {
  return await getBackend<VertriebshistorieDetailResult>(
    `/api/v1/vertriebshistorie/${encodeURIComponent(companyId)}`
  );
}

// =============================================================================
// Partnervereine — Standorte, Umkreissuche, Karte (Vertrieb)
// =============================================================================

/** Wie der Standort zustande kam — "naeherung" kann zweistellige km danebenliegen. */
export type PartnervereinGeoQuelle = "plz" | "ort" | "naeherung" | "";

export type PartnervereinZeile = {
  company_id: string;
  verein: string;
  plz: string;
  ort: string;
  bundesland: string;
  lat: number | null;
  lon: number | null;
  geo_quelle: PartnervereinGeoQuelle;
  /** Rohwert der Quelle — zeigt, was normalisiert wurde (z.B. "8141" → "08141"). */
  plz_roh: string;
  /** "stripe" | "hubspot" | "" — wo die Adresse zu korrigieren wäre. */
  adress_quelle: string;
};

export type PartnervereineGuete = {
  gesamt: number;
  verortet: number;
  exakt: number;
  ueber_ort: number;
  genaehert: number;
  ohne_standort: number;
  ohne_standort_namen: string[];
  aus_stripe: number;
  aus_hubspot: number;
  plz_tabelle_zeilen: number;
  stand: number;
};

export type PartnervereineListe = {
  zeilen: PartnervereinZeile[];
  je_bundesland: Array<{ bundesland: string; anzahl: number }>;
  guete: PartnervereineGuete;
};

export type PartnervereinUmkreisTreffer = PartnervereinZeile & { entfernung_km: number };

export type PartnervereineUmkreis = {
  ausgangspunkt: { plz: string; ort: string; bundesland: string; lat: number; lon: number };
  radius_km: number;
  treffer: PartnervereinUmkreisTreffer[];
  ohne_standort: number;
};

/** GET /api/v1/vertrieb/partnervereine — alle aktiven Partnervereine mit Standort. */
export async function readPartnervereine(): Promise<PartnervereineListe> {
  return await getBackend<PartnervereineListe>("/api/v1/vertrieb/partnervereine");
}

/**
 * GET /api/v1/vertrieb/partnervereine/umkreis — Partnervereine um eine PLZ.
 *
 * Wirft ApiError mit code "NOT_FOUND", wenn die PLZ nicht in der PLZ-Tabelle
 * steht. Das ist bewusst KEIN leeres Ergebnis: „diese PLZ kennen wir nicht" und
 * „hier ist kein Partnerverein" sind verschiedene Auskünfte.
 */
export async function readPartnervereineUmkreis(
  plz: string,
  km: number
): Promise<PartnervereineUmkreis> {
  return await getBackend<PartnervereineUmkreis>("/api/v1/vertrieb/partnervereine/umkreis", { plz, km });
}


// =============================================================================
// E-Mail/Passwort-Auth (public — alle noAuth, kein Bearer/Redirect)
// =============================================================================

export type LoginResult = { token: string; exp: number; email: string };
export type InviteValidateResult = { valid: boolean; email?: string; vollname?: string };

/** POST /api/v1/auth/login — E-Mail/Passwort. remember=true → 30-Tage-Session. */
export async function apiLogin(email: string, password: string, remember: boolean): Promise<LoginResult> {
  return await callBackend<LoginResult>(
    "/api/v1/auth/login",
    { email, password, remember },
    { noAuth: true, skipAutoLogout: true }
  );
}

/** GET /api/v1/auth/invite/validate — Token prüfen + Prefill (E-Mail/Name). */
export async function validateInvite(token: string): Promise<InviteValidateResult> {
  return await getBackend<InviteValidateResult>(
    "/api/v1/auth/invite/validate",
    { token },
    { noAuth: true }
  );
}

/** POST /api/v1/auth/activate — Passwort setzen (+ optional E-Mail-Wechsel) → Auto-Login. */
export async function apiActivate(token: string, email: string, password: string): Promise<LoginResult> {
  return await callBackend<LoginResult>(
    "/api/v1/auth/activate",
    { token, email, password },
    { noAuth: true, skipAutoLogout: true }
  );
}

/** POST /api/v1/auth/forgot — Reset anstoßen (immer neutrale Antwort). */
export async function apiForgotPassword(email: string): Promise<{ message: string }> {
  return await callBackend<{ message: string }>(
    "/api/v1/auth/forgot",
    { email },
    { noAuth: true, skipAutoLogout: true }
  );
}

/** POST /api/v1/auth/reset — neues Passwort mit Reset-Token. */
export async function apiResetPassword(token: string, password: string): Promise<{ email: string }> {
  return await callBackend<{ email: string }>(
    "/api/v1/auth/reset",
    { token, password },
    { noAuth: true, skipAutoLogout: true }
  );
}

// =============================================================================
// Self-Service Konto (authentifiziert): eigenes Passwort / eigene Login-E-Mail
// =============================================================================

/** POST /profile/change-password — eigenes Passwort ändern (current nur nötig, wenn bereits gesetzt). */
export async function changePassword(
  currentPassword: string | undefined,
  newPassword: string
): Promise<{ ok: boolean; had_password: boolean }> {
  return await callBackend<{ ok: boolean; had_password: boolean }>(
    "/profile/change-password",
    { current_password: currentPassword, new_password: newPassword }
  );
}

/** POST /profile/change-email — eigene Login-E-Mail ändern. Liefert frisches Token zurück. */
export async function changeEmail(
  newEmail: string,
  currentPassword?: string
): Promise<{ email: string; token?: string; exp?: number; unchanged?: boolean }> {
  return await callBackend<{ email: string; token?: string; exp?: number; unchanged?: boolean }>(
    "/profile/change-email",
    { new_email: newEmail, current_password: currentPassword }
  );
}

// =============================================================================
// Nachfolgeregelung (E10 / AUS Baustein C) — gepflegt vom Bereichsleiter.
// =============================================================================

export type NachfolgeRolle = "caller" | "host_mt" | "host_fu" | "kb_betreuer" | "owner";

export type NachfolgeRegel = {
  personId: string;
  rolle: NachfolgeRolle;
  nachfolgerPersonId: string;
  gueltigAb: string;
  notiz: string;
  updatedAt: number;
  bereich: string;
  personName: string;
  nachfolgerName: string;
  /** false = Regel läuft ins Leere (Nachfolger inzwischen selbst raus). */
  nachfolgerAktiv: boolean;
};

export async function nachfolgeList(personId?: string): Promise<{ regeln: NachfolgeRegel[]; rollen: NachfolgeRolle[] }> {
  return await callBackend("/api/v1/nachfolge/list", personId ? { personId } : {});
}

export async function nachfolgeSet(p: {
  personId: string; rolle: NachfolgeRolle; nachfolgerPersonId: string; gueltigAb?: string; notiz?: string;
}): Promise<{ ok: true; warnung?: string }> {
  return await callBackend("/api/v1/nachfolge/set", p as unknown as Record<string, unknown>);
}

export async function nachfolgeDelete(personId: string, rolle: NachfolgeRolle): Promise<{ ok: true }> {
  return await callBackend("/api/v1/nachfolge/delete", { personId, rolle });
}

// =============================================================================
// Übergabe-Vorschau (E10 / AUS Baustein D) — Arbeitsliste für den Bereichsleiter.
// =============================================================================

export type UebergabeKategorie =
  | "pflegeluecken" | "fus" | "meetings" | "slots" | "verfuegbarkeit"
  | "hubspot_companies" | "hubspot_tasks" | "kb_vereine" | "kb_aufgaben";

export type UebergabeZeile = {
  kategorie: UebergabeKategorie;
  id: string;
  bezeichnung: string;
  datum: string;
  rolle: NachfolgeRolle;
  aktuellPersonId: string;
  /** Warum die Zeile in der Liste steht — für den BL sichtbar. */
  grund: string;
  nurAnzeige?: boolean;
  vorschlagPersonId: string;
  vorschlagName: string;
  /** false = Apply für diese Kategorie noch nicht gebaut (nur Anzeige). */
  applyBereit: boolean;
};

export type UebergabeVorschau = {
  personId: string;
  personName: string;
  vertragsende: string;
  stichtag: string;
  zeilen: UebergabeZeile[];
  zaehler: Record<string, number>;
  nichtSchreibbar: string[];
};

export async function uebergabePreview(personId: string, stichtag?: string): Promise<UebergabeVorschau> {
  return await callBackend("/api/v1/uebergabe/preview", stichtag ? { personId, stichtag } : { personId });
}

export type UebergabeApplyErgebnis = {
  ergebnis: Array<{ kategorie: string; id: string; ok: boolean; hinweis?: string }>;
  geschrieben: number;
  gesamt: number;
  auditId: string;
};

export async function uebergabeApply(p: {
  personId: string;
  stichtag?: string;
  /** Tipp-Bestätigung: exakter Name der ausscheidenden Person. */
  bestaetigung: string;
  auswahl: Array<{ kategorie: string; id: string; zielPersonId: string }>;
}): Promise<UebergabeApplyErgebnis> {
  return await callBackend("/api/v1/uebergabe/apply", p as unknown as Record<string, unknown>);
}

// =============================================================================
// Host-Sperrzeiten (Baustein 6b) + Vertretung klären (E11)
// =============================================================================

export type Sperrzeit = {
  sperrzeitId: string;
  personId: string;
  wochentag: number;
  /**
   * 🆕 17.09.2026 — YYYY-MM-DD: gilt NUR an diesem Tag. Leer = Wochentagsregel.
   * 🔴 Das Backend liefert das Feld seit dem 24.08.; dieser Typ kannte es nicht,
   * und damit war die Einzeltag-Sperre von der Oberfläche aus unerreichbar.
   */
  datum?: string;
  von: string;
  bis: string;
  gueltigVon: string;
  gueltigBis: string;
  notiz: string;
  angelegtVon: string;
  angelegtAm: string;
  /** Die betroffenen Startzeiten — macht die exklusive Obergrenze sichtbar. */
  startzeiten: string[];
};

export type FreierHost = { personId: string; name: string; imRaster: boolean };

export type Klaerungsposten = {
  key: string;
  bezug: "meeting" | "fu" | "slot";
  id: string;
  datum: string;
  uhrzeit: string;
  hostPersonId: string;
  hostName: string;
  verein: string;
  grund: "urlaub" | "krank" | "sperrzeit" | "doppelt";
  quelleId: string;
  quelleText: string;
  freieHosts: FreierHost[];
  /** Termin ohne Uhrzeit ⇒ die Frei-Liste ist nicht berechenbar (≠ „niemand frei"). */
  zeitFehlt: boolean;
};

export type RasterWiderspruch = { datum: string; uhrzeit: string; hostPersonId: string; sperrzeitId: string };

/**
 * Eine laufende oder kommende Abwesenheit — **Anzeige, kein Eintrag.**
 * 🔑 Urlaub wird NICHT als Sperrzeit geschrieben (er wirkt bereits, und zwar
 * für jede Stunde des Tages). Er steht hier, damit eine leere Sperrzeit-Woche
 * nicht als „ich muss noch nachtragen" missverstanden wird.
 */
export type AbwesenheitAnzeige = {
  personId: string; von: string; bis: string;
  grund: "urlaub" | "krank"; quelleId: string;
};

export async function sperrzeitenList(p: { person_id?: string; alle?: boolean } = {}): Promise<{
  person_id: string; darf_fuer_alle: boolean; namen: Record<string, string>; eintraege: Sperrzeit[];
  abwesenheiten: AbwesenheitAnzeige[];
}> {
  return await callBackend("/api/v1/sperrzeiten/list", p as unknown as Record<string, unknown>);
}

export async function sperrzeitenUpsert(p: {
  person_id?: string; wochentag: number; von: string; bis: string;
  /**
   * 🆕 17.09.2026 — YYYY-MM-DD: die Sperre gilt **nur an diesem Tag**.
   *
   * 🔴 **Das Feld war seit dem 24.08. tot.** Migration 0080 und der Endpunkt
   * können es seitdem; die Oberfläche kannte es nicht, also gab es keinen Weg,
   * einen einzelnen Tag aus einer Serie zu nehmen — genau das, woran Maxi
   * hängenblieb: *„ich will es ja nicht ganz rausnehmen, ich will nur sagen:
   * an dem Tag nicht."*
   *
   * Leer = Wochentagsregel wie bisher. Bei gesetztem Datum sind `wochentag`
   * und `gueltig_*` bedeutungslos — das Datum IST die Aussage.
   */
  datum?: string;
  gueltig_von?: string; gueltig_bis?: string; notiz?: string; prev_id?: string;
}): Promise<{
  id: string; action: "added" | "updated"; startzeiten: string[];
  klaerung: Klaerungsposten[]; raster_widerspruch: RasterWiderspruch[]; auditId: string;
}> {
  return await callBackend("/api/v1/sperrzeiten/upsert", p as unknown as Record<string, unknown>);
}

export async function sperrzeitenDelete(id: string): Promise<{ deleted: boolean; auditId: string }> {
  return await callBackend("/api/v1/sperrzeiten/delete", { id });
}

// =============================================================================
// BAUSTEIN 4 — die eigene Buchungsstrecke (E12b-7)
// =============================================================================

export type BuchungZeitpunkt = { datum: string; uhrzeit: string };

export type BuchungAngebot = {
  zeitpunkte: BuchungZeitpunkt[];
  /**
   * 🔑 **Zwei Zahlen, nicht eine.** Eine leere Liste darf nicht zweierlei
   * heißen können — „es gibt keinen freien Slot" und „es gab welche, aber alle
   * Hosts sind abwesend oder gesperrt". Genau diese Verwechslung ist der
   * `slot_pool_size`-Fehler, und die Oberfläche zeigt die Differenz deshalb an.
   */
  zeitpunkte_gesamt: number;
  zeitpunkte_angeboten: number;
  gefiltert: { abwesend: number; sperrzeit: number; feiertag: number; vorlauf: number };
  /**
   * 🔒 **Wie viele Slots je Zeitpunkt noch offen sind — nur intern.**
   *
   * > **Justin, 17.08.:** *„für den Caller macht es Sinn zu sehen, wie viele
   * > Slots noch offen sind — nicht mal zwingend die Berater."*
   *
   * Schlüssel `YYYY-MM-DDTHH:MM`. Gezählt werden **Slots**, keine Personen:
   * der Caller sieht beim Telefonat, ob zu 14:00 noch Spielraum ist.
   * ⚠️ Die öffentliche Buchungsseite bekommt dieses Feld nicht (R1) — es
   * entsteht dort serverseitig gar nicht erst, weil `mitAnzahl` ungesetzt bleibt.
   */
  frei_je_zeitpunkt: Record<string, number>;
};

export async function buchungAngebot(): Promise<BuchungAngebot> {
  return await callBackend("/api/v1/b2/buchungAngebot", {});
}

export type FreierHostZeile = { person_id: string; name: string; im_raster: boolean };
export type BelegterHostZeile = { person_id: string; name: string; grund: string };

/** ⑧ E8: die übrigen Hosts mit dem Grund als KATEGORIE — nie mit fremdem Titel. */
export async function buchungFreieHosts(datum: string, uhrzeit: string): Promise<{
  datum: string; uhrzeit: string;
  hosts: FreierHostZeile[]; belegt: BelegterHostZeile[]; feiertag: boolean;
}> {
  return await callBackend("/api/v1/b2/freieHosts", { datum, uhrzeit });
}

export type BuchungEingabe = {
  datum: string; uhrzeit: string;
  company_id: string; verein_anzeigename?: string;
  kontakt_email: string; kontakt_name?: string; kontakt_telefon?: string;
  whatsapp_optin?: boolean;
  anzahl_teams_range?: string; notiz?: string;
  /**
   * Wer terminiert hat. Leer = der angemeldete Nutzer.
   * 🔑 Nötig, weil ein BL auch FÜR einen Caller bucht — ohne dieses Feld zählte
   * die Terminierung dem, der klickt.
   */
  caller_person_id?: string;
  /** Nur beim Buchen AUSSERHALB des Rasters — dann ist auch der Host Pflicht. */
  override_grund?: string; host_person_id?: string;
  mehrsparten?: string; fussballsparte?: string; groesse?: string;
};

export async function buchungBuchen(p: BuchungEingabe): Promise<{
  action: string; meeting_id: string; host_person_id: string; slot_id: string;
  slot_geschrieben: boolean; slot_override: boolean;
  kalender: { ok: boolean; event_id: string; meet_link: string; grund?: string };
  aenderungs_link: string; ownership_sync: string; lead_status: string; auditId: string;
}> {
  return await callBackend("/api/v1/b2/buchen", p as unknown as Record<string, unknown>);
}

export type InboundAnfrage = {
  id: string; eingangAm: string; vereinName: string; companyId: string;
  companyTreffer: string; kontaktName: string; kontaktEmail: string; kontaktTelefon: string;
  anliegen: string; callerPersonId: string; status: string;
  erledigtAm: string; erledigtVon: string; ergebnisNotiz: string;
  leadStatusPush: string; quelle: string;
};

export async function anfragenList(p: { status?: string; caller_person_id?: string } = {}): Promise<{
  anfragen: InboundAnfrage[]; anzahl: number;
}> {
  return await callBackend("/api/v1/b2/anfragen/list", p as unknown as Record<string, unknown>);
}

export async function anfrageAbschliessen(
  id: string, status: "erledigt" | "verworfen", notiz: string
): Promise<{ action: string; id: string }> {
  return await callBackend("/api/v1/b2/anfragen/abschliessen", { id, status, notiz });
}

export async function anfrageZuweisen(id: string, caller_person_id: string): Promise<{ action: string }> {
  return await callBackend("/api/v1/b2/anfragen/zuweisen", { id, caller_person_id });
}

export type VertretungVorschau = {
  istBl: boolean;
  stand: string;
  zeilen: Klaerungsposten[];
  zaehler: Record<string, number>;
  raster_widerspruch: RasterWiderspruch[];
  hinweis_frei_liste: string;
};

export async function vertretungPreview(): Promise<VertretungVorschau> {
  return await callBackend("/api/v1/vertretung/preview", {});
}

/**
 * Sammel-Neuterminierung (Justin 16.09.2026): dieselbe Auswahl wie
 * `vertretungApply` darunter, anderer Ausgang. Der Grund geht WÖRTLICH in die
 * Slack-Nachricht an den Caller — deshalb ist er Pflicht, hier wie im Endpunkt.
 * 📏 Gebaut, weil am 16.09. 14 von 17 betroffenen Terminen gar keine Vertretung hatten.
 */
export async function vertretungNeuterminieren(auswahl: string[], grund: string): Promise<{
  /** `in_zeeg_aendern`: gespeichert, aber ein Zeeg-Termin vor Beginn — „Verein (Datum Uhrzeit)". */
  ergebnis: Array<{ key: string; ok: boolean; hinweis?: string; in_zeeg_aendern?: string }>;
  geschrieben: number; gesamt: number; auditId: string;
}> {
  return await callBackend("/api/v1/vertretung/neuterminieren", { auswahl, grund });
}

export async function vertretungApply(auswahl: Array<{ key: string; zielPersonId: string }>): Promise<{
  ergebnis: Array<{ key: string; ok: boolean; hinweis?: string }>;
  geschrieben: number; gesamt: number; auditId: string;
}> {
  return await callBackend("/api/v1/vertretung/apply", { auswahl });
}

// =============================================================================
// VERTRETUNGS-VORGÄNGE — „eine Abwesenheit, einmal geregelt" (Justin 24.08.)
// =============================================================================

export type VertretungsRolle = "host_mt" | "host_fu" | "caller";

export type UngedeckteSpanne = { von: string; bis: string; grund: string };

export type VorgangRolle = {
  rolle: VertretungsRolle;
  betroffen: number;
  /** Nur für die Host-Rollen gefüllt — die Caller-Seite hat keinen Schreibpfad. */
  keys: string[];
  vertreterPersonId: string;
  vertreterName: string;
  regelId: string;
  /** 🔴 Tage, an denen der Vertreter SELBST abwesend ist. Leer = gedeckt. */
  ungedeckt: UngedeckteSpanne[];
};

export type VertretungsVorgang = {
  key: string;
  personId: string;
  name: string;
  grund: "urlaub" | "krank";
  von: string;
  bis: string;
  quelleId: string;
  rollen: VorgangRolle[];
  offeneRollen: number;
};

/**
 * Eine Vertretungsregel, wie die Ansicht sie zeigt.
 * 🔑 `quelle: "manuell"` heißt: sie hängt an KEINER Abwesenheit (Fortbildung,
 * Überlastung, Elternzeit) und käme in der Vorgangsliste deshalb nie vor.
 */
export type VertretungsRegelZeile = {
  regelId: string;
  personId: string;
  name: string;
  rolle: VertretungsRolle;
  vertreterPersonId: string;
  vertreterName: string;
  von: string;
  bis: string;
  quelle: "urlaub" | "krank" | "manuell";
  notiz: string;
  /** Gilt sie heute? */
  laeuft: boolean;
};

export type VertretungsVorgaenge = {
  istBl: boolean;
  stand: string;
  vorgaenge: VertretungsVorgang[];
  kandidaten: Record<string, Array<{ personId: string; name: string }>>;
  regeln: VertretungsRegelZeile[];
  hinweis: string;
};

export async function vertretungVorgaenge(): Promise<VertretungsVorgaenge> {
  return await callBackend("/api/v1/vertretung/vorgaenge", {});
}

export async function vertretungRegelUpsert(p: {
  person_id: string; rolle: string; vertreter_person_id: string;
  von: string; bis: string; quelle: string; quelle_id: string; notiz: string; prev_id?: string;
}): Promise<{ id: string; action: "added" | "updated"; ungedeckt: UngedeckteSpanne[]; auditId: string }> {
  return await callBackend("/api/v1/vertretung/regel-upsert", p as unknown as Record<string, unknown>);
}

export async function vertretungRegelDelete(id: string): Promise<{ deleted: boolean; auditId: string }> {
  return await callBackend("/api/v1/vertretung/regel-delete", { id });
}

/**
 * Was eine Vertretungsregel bewirken WÜRDE — vor dem Speichern.
 *
 * 🔴 Alle drei Warnungen gab es vorher schon, nur zu spät: `regelUpsert` weist
 * die Überlappung mit 409 ab, prüft den Vertreter auf „aktiv" und liefert die
 * Deckungslücke **im Ergebnis** — man erfährt „dein Vertreter ist selbst im
 * Urlaub" also **nachdem** die Regel steht.
 * 🔑 *Der Wert lag an der Quelle und wurde eine Ebene später benutzt.*
 *
 * `vertreterAktiv` ist `null`, solange kein Vertreter gewählt ist — das ist
 * **nicht** dasselbe wie `false`. Eine Vorschau ohne Vertreter kann über ihn
 * nichts sagen, und „nicht aktiv" wäre die falsche Auskunft.
 */
export type VertretungsRegelVorschau = {
  betroffen: number;
  termine: Array<{ key: string; datum: string }>;
  /** Wie viele Termine die Liste NICHT zeigt — damit eine gekappte Liste nicht wie alle aussieht. */
  gekappt: number;
  kollision: { von: string; bis: string; vertreterPersonId: string } | null;
  vertreterAktiv: boolean | null;
  vertreterName: string;
  ungedeckt: UngedeckteSpanne[];
  stand: string;
};

export async function vertretungRegelVorschau(p: {
  person_id: string; rolle: string; vertreter_person_id: string;
  von: string; bis: string; prev_id?: string;
}): Promise<VertretungsRegelVorschau> {
  return await callBackend("/api/v1/vertretung/regel-vorschau", p as unknown as Record<string, unknown>);
}

/** Sammel-Bestätigung: alle Termine eines Vorgangs in einer Rolle. */
export async function vertretungVorgangApply(vorgang_key: string, rolle: string): Promise<{
  ergebnis: Array<{ key: string; ok: boolean; hinweis?: string }>;
  geschrieben: number; gesamt: number; auditId: string;
}> {
  return await callBackend("/api/v1/vertretung/vorgang-apply", { vorgang_key, rolle });
}

/**
 * Nur die Zahl für das Sidebar-Signal — bewusst ein eigener, schlanker
 * Endpunkt statt `vertretungPreview().zeilen.length` (die Vorschau rechnet je
 * Posten die Frei-Liste aus).
 */
export async function vertretungCount(): Promise<{ istBl: boolean; offen: number; eigene: number | null }> {
  return await callBackend("/api/v1/vertretung/count", {});
}

/**
 * HubSpot-Rechte prüfen (Admin, 26.08.).
 *
 * 🔴 **Dieser Aufruf SCHREIBT nach HubSpot** — er legt Testobjekte an einem
 * Testverein an und löscht sie wieder. Deshalb kein beiläufiger Knopf: die
 * Oberfläche fragt vorher nach.
 */
export type HubspotRechteBefund = {
  objekt: "notes" | "emails" | "tasks";
  scope: string;
  anlegen: "ok" | "scope_fehlt" | "fehler";
  verknuepfen: "ok" | "fehler" | "nicht_geprueft";
  aufgeraeumt: boolean;
  restId: string;
  status: number;
  meldung: string;
};

export async function adminHubspotRechte(): Promise<{
  companyId: string; verein: string; befunde: HubspotRechteBefund[]; zusammenfassung: string;
}> {
  return await callBackend("/api/v1/admin/hubspot/rechte", {});
}

// =============================================================================
// Sekretariat (JV-9) — persönliche Protokoll-Entwürfe
// =============================================================================

/** Ein unfreigegebener Protokoll-Entwurf aus dem Drive-Ordner `_Entwürfe`. */
export type ProtokollEntwurf = {
  id: string;
  titel: string;
  geaendert?: string;
  link?: string;
};

/** POST /api/v1/sekretariat/entwuerfe — liest nur, schreibt nichts. */
export async function sekretariatEntwuerfe(): Promise<{
  anzahl: number;
  entwuerfe: ProtokollEntwurf[];
}> {
  return await callBackend("/api/v1/sekretariat/entwuerfe", {});
}

/**
 * POST /api/v1/sekretariat/freigeben — gibt einen Entwurf zur Ablage frei.
 *
 * 🔑 `angenommen: true` heisst NICHT "abgelegt". Make quittiert auch dann, wenn
 * das Szenario danach scheitert. Der Beleg ist, dass der Entwurf beim nächsten
 * Laden aus der Liste verschwunden ist.
 */
export async function sekretariatFreigeben(
  fileId: string,
  titel: string
): Promise<{ angenommen: boolean; fileId: string; hinweis: string }> {
  return await callBackend("/api/v1/sekretariat/freigeben", { fileId, titel });
}
